import {NetworkStatus} from '../../features/network/Network';
import StationModeSettings from './StationModeSettings';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AuthContext, SuccessNotification } from '../../App';
import { ExtendedOrderHistoryTab } from '../Admin/AdminComponents';
import DashboardSidebar from '../Common/DashboardSidebar';
import ScrollToTop from '../Common/ScrollToTop';
import PaymentAccessOverrideControl from './PaymentAccessOverrideControl';
import PaymentDepartmentMapping from './PaymentDepartmentMapping';
import { Users, FileText, Building2, Settings, Calendar, MessageSquare, Star, RefreshCw, Search, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const DeveloperDashboard = () => {
  // Load active tab from localStorage or default to 'employees'
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('developerDashboard_activeTab') || 'employees';
  });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]); // Store all departments
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeProfile, setShowEmployeeProfile] = useState(false);
  
  // Extended Order History state
  const [extendedOrderHistory, setExtendedOrderHistory] = useState([]);
  const [extendedOrderHistoryPagination, setExtendedOrderHistoryPagination] = useState({
    page: 1,
    page_size: 30,
    total: 0,
    total_pages: 1,
  });
  const [selectedDeptForHistory, setSelectedDeptForHistory] = useState('');
  
  // NEU: Help Content Editor State
  const [helpHtmlContent, setHelpHtmlContent] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  
  // Success Notification State
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  const { logout } = React.useContext(AuthContext);

  // Helper function to get department name from ID
  const getDepartmentName = (departmentId) => {
    // DYNAMICALLY extracts department number from ID (e.g., fw4abteilung1 OR fw9100abteilung1 → "Wa. 1.")
    // Supports multi-tenant setups with any wache_nummer
    const match = departmentId.match(/abteilung(\d+)$/);
    if (match) {
      return `Wa. ${match[1]}.`;
    }
    return departmentId; // Fallback to original ID if pattern doesn't match
  };

  // Persist active tab to localStorage
  useEffect(() => {
    localStorage.setItem('developerDashboard_activeTab', activeTab);
  }, [activeTab]);

  // NEU: Load help content when tab is opened
  useEffect(() => {
    if (activeTab === 'help-editor') {
      fetchHelpContent();
    }
  }, [activeTab]);


  useEffect(() => {
    fetchAllEmployees();
    fetchHelpContent(); // Load help content on mount
  }, []);

  const fetchAllEmployees = async () => {
    try {
      // Fetch employees from all departments
      const deptResponse = await axios.get(`${API}/departments`);
      const departments = deptResponse.data;
      
      // Store departments for the dropdown
      setAllDepartments(departments);
      
      let allEmps = [];
      
      // Fetch 8H-Service employees first (using first department as reference)
      if (departments.length > 0) {
        try {
          const eightHResponse = await axios.get(`${API}/departments/${departments[0].id}/8h-employees`);
          const eightHEmployees = eightHResponse.data.map(emp => ({
            id: emp.id,
            name: emp.name,
            department_id: emp.department_id,
            department_name: '8H-Dienst',
            is_8h_service: true,
            breakfast_balance: 0,
            drinks_sweets_balance: 0
          }));
          allEmps = [...eightHEmployees];
        } catch (error) {
          console.error('Fehler beim Laden der 8H-Mitarbeiter:', error);
        }
      }
      
      // Then fetch regular employees from all departments
      for (const dept of departments) {
        const empResponse = await axios.get(`${API}/departments/${dept.id}/employees`);
        const employees = empResponse.data.map(emp => ({
          ...emp,
          department_name: dept.name,
          department_id: dept.id
        }));
        allEmps = [...allEmps, ...employees];
      }
      
      // Sort: 8H first, then by department, then by name
      allEmps.sort((a, b) => {
        // 8H employees come first
        if (a.is_8h_service && !b.is_8h_service) return -1;
        if (!a.is_8h_service && b.is_8h_service) return 1;
        
        // Then sort by department
        if (a.department_name !== b.department_name) {
          return a.department_name.localeCompare(b.department_name);
        }
        
        // Finally sort by name
        return a.name.localeCompare(b.name);
      });
      
      setAllEmployees(allEmps);
    } catch (error) {
      console.error('Fehler beim Laden aller Mitarbeiter:', error);
    }
  };
  
  const fetchExtendedOrderHistory = async (page = 1, deptId = selectedDeptForHistory) => {
    if (!deptId) return;
    try {
      const response = await axios.get(`${API}/department-admin/extended-order-history/${deptId}?page=${page}&page_size=30`);
      setExtendedOrderHistory(response.data.orders);
      setExtendedOrderHistoryPagination({
        page: response.data.page || 1,
        page_size: response.data.page_size || 30,
        total: response.data.total || 0,
        total_pages: response.data.total_pages || 1,
      });
      return response.data;
    } catch (error) {
      console.error('Fehler beim Laden der erweiterten Bestellhistorie:', error);
    }
  };

  // NEU: Fetch and Save Help Content
  const fetchHelpContent = async () => {
    try {
      const response = await axios.get(`${API}/help-content`);
      setHelpHtmlContent(response.data.html_content);
    } catch (error) {
      console.error('Fehler beim Laden der Hilfe-Inhalte:', error);
    }
  };
  
  const saveHelpContent = async () => {
    try {
      await axios.post(`${API}/developer/help-content`, {
        html_content: helpHtmlContent
      });
      setSuccessMessage('✅ Hilfe-Inhalte erfolgreich gespeichert!');
      setShowSuccessNotification(true);
    } catch (error) {
      console.error('Fehler beim Speichern der Hilfe-Inhalte:', error);
      alert('Fehler beim Speichern: ' + (error.response?.data?.detail || error.message));
    }
  };


  // Get all departments except the current one for move employee dropdown
  const getAllOtherDepartments = (currentDepartmentId) => {
    return allDepartments.filter(dept => dept.id !== currentDepartmentId);
  };

  const tabs = [
    { id: 'employees', label: 'Erweiterte Mitarbeiterverwaltung' },
    { id: 'extended-history', label: 'Erweiterter Verlauf' },
    { id: 'department-management', label: 'Wachabteilung verwalten' },
    { id: 'general-settings', label: 'Allg. Einstellungen' },
    { id: 'balance-audit', label: 'Saldo-Diagnose' },
    { id: 'help-editor', label: 'Hilfe bearbeiten' },
    { id: 'feedback-management', label: '💬 Feedback System' }, // NEW
    { id: 'payment-integration', label: '💳 Payment Integration' },
    { id: 'deletion-log', label: 'Lösch-Log' },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#F5F5F5' }}>
      {/* Sidebar Navigation */}
      <DashboardSidebar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        menuItems={[
          { id: 'employees', label: 'Mitarbeiter-Verwaltung', icon: Users },
          { id: 'extended-history', label: 'Bestellverlauf', icon: Calendar },
          { id: 'department-management', label: 'Abteilungen-Verwaltung', icon: Building2 },
          { id: 'general-settings', label: 'Allgemeine Einstellungen', icon: Settings },
          { id: 'balance-audit', label: 'Saldo-Diagnose', icon: Search },
          { id: 'help-editor', label: 'Hilfe-Editor', icon: FileText },
          { id: 'feedback-management', label: 'Feedback System', icon: MessageSquare },
          { id: 'payment-integration', label: 'Payment Integration', icon: Settings },
          { id: 'deletion-log', label: 'Lösch-Log', icon: Trash2 }
        ]}
        dashboardTitle="Developer Dashboard"
        onLogout={logout}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={setIsSidebarCollapsed}
      />

      <div className={`transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'} px-4 sm:px-6 lg:px-8 py-8`}>
        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
        {activeTab === 'employees' && (
          <ExtendedEmployeeManagementTab 
            employees={allEmployees}
            allDepartments={allDepartments}
            getAllOtherDepartments={getAllOtherDepartments}
            onEmployeeUpdate={fetchAllEmployees}
            setSelectedEmployee={setSelectedEmployee}
            setShowEmployeeProfile={setShowEmployeeProfile}
          />
        )}
        
        {activeTab === 'extended-history' && (
          <ExtendedOrderHistoryTab 
            extendedOrderHistory={extendedOrderHistory}
            historyPagination={extendedOrderHistoryPagination}
            fetchExtendedOrderHistory={fetchExtendedOrderHistory}
            selectedDeptForHistory={selectedDeptForHistory}
            setSelectedDeptForHistory={setSelectedDeptForHistory}
          />
        )}
        
        {activeTab === 'department-management' && (
          <DepartmentManagementTab 
            setSuccessMessage={setSuccessMessage}
            setShowSuccessNotification={setShowSuccessNotification}
          />
        )}
        
        {activeTab === 'general-settings' && <NetworkStatus apiBase={API} />}
        {activeTab === 'general-settings' && (
          <GeneralSettingsTab />
        )}
        
        {activeTab === 'balance-audit' && (
          <BalanceAuditTab allEmployees={allEmployees} />
        )}
        
        {activeTab === 'help-editor' && (
          <HelpEditorTab 
            helpHtmlContent={helpHtmlContent}
            setHelpHtmlContent={setHelpHtmlContent}
            showPreview={showPreview}
            setShowPreview={setShowPreview}
            saveHelpContent={saveHelpContent}
          />
        )}
        
        {activeTab === 'feedback-management' && (
          <FeedbackManagementTab />
        )}
        
        {activeTab === 'payment-integration' && (
          <PaymentIntegrationTab />
        )}

        {activeTab === 'deletion-log' && (
          <DeletionLogTab />
        )}


        {/* Employee Profile Modal */}
        {showEmployeeProfile && selectedEmployee && (
          <DeveloperEmployeeProfile
            employee={selectedEmployee}
            onClose={() => {
              setShowEmployeeProfile(false);
              setSelectedEmployee(null);
            }}
            onRefresh={fetchAllEmployees}
          />
        )}
        
        {/* Success Notification */}
        {showSuccessNotification && (
          <SuccessNotification
            message={successMessage}
            onClose={() => {
              setShowSuccessNotification(false);
              setSuccessMessage('');
            }}
          />
        )}
        </div>
      </div>
    </div>
  );
};

// History Display Component for Developer Dashboard
const HistoryDisplay = ({ employeeProfile, formatDate, deleteHistoryEntry }) => {
  const orders = employeeProfile.order_history || [];
  const payments = employeeProfile.payment_history || [];
  
  // Helper function to format department name
  const formatDepartmentName = (deptId) => {
    if (!deptId) return deptId;
    const match = deptId.match(/abteilung(\d+)$/);
    if (match) {
      return `WA ${match[1]}`;
    }
    return deptId;
  };
  
  // Combine both arrays and sort by timestamp
  const allEntries = [
    ...orders.map(order => ({...order, type: 'order'})),
    ...payments.map(payment => ({...payment, type: 'payment'}))
  ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  
  if (allEntries.length === 0) {
    return <p className="text-gray-600 text-center py-8">Keine Einträge vorhanden</p>;
  }
  
  return (
    <div className="space-y-4">
      {allEntries.map((entry, index) => (
        <div key={entry.id || index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-start mb-3">
            <div className="flex-1">
              {entry.type === 'order' ? (
                <>
                  <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded">
                    Bestellung
                  </span>
                  <span className="text-sm text-gray-600">{formatDate(entry.timestamp)}</span>
                </>
              ) : (
                <>
                  <span className="inline-block bg-green-100 text-green-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded">
                    {entry.amount > 0 ? 'Einzahlung' : 'Auszahlung'}
                  </span>
                  {/* Department Badge for payments */}
                  {entry.department_id && employeeProfile && entry.department_id !== employeeProfile.department_id && (
                    <span className="inline-block bg-purple-100 text-purple-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded">
                      {formatDepartmentName(entry.department_id)}
                    </span>
                  )}
                  <span className="text-sm text-gray-600">{formatDate(entry.timestamp)}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <p className={`font-semibold ${
                entry.type === 'payment' 
                  ? (entry.amount >= 0 ? 'text-green-600' : 'text-red-600')
                  : (entry.total_price < 0 ? 'text-red-600' : 'text-gray-900')
              }`}>
                {entry.type === 'payment' 
                  ? `${entry.amount > 0 ? '+' : ''}${entry.amount.toFixed(2)}€` 
                  : `${entry.total_price < 0 ? '-' : ''}${Math.abs(entry.total_price || 0).toFixed(2)}€`
                }
              </p>
              <button
                onClick={() => deleteHistoryEntry(entry.id, entry.type)}
                className="bg-red-500 text-white px-2 py-1 text-xs rounded hover:bg-red-600"
              >
                🔧 Löschen
              </button>
            </div>
          </div>
          
          {entry.type === 'order' && entry.readable_items && (
            <div className="space-y-1">
              {entry.readable_items.map((item, idx) => (
                <div key={idx} className="text-sm flex justify-between items-start">
                  <div className="flex-1">
                    <span className="font-medium">{item.description}</span>
                    {item.toppings && <span className="text-gray-600 block text-xs">mit {item.toppings}</span>}
                  </div>
                  {item.total_price && (
                    <span className="text-sm font-medium text-right ml-2">{item.total_price}</span>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {entry.type === 'payment' && (
            <div className="mt-2">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Buchungstyp:</span> {
                  entry.balance_type === 'combined' 
                    ? 'Gesamtkonto' 
                    : (entry.balance_type === 'breakfast' ? 'Frühstück/Mittag' : 'Getränke/Snacks')
                }
              </div>
            </div>
          )}
          
          {entry.notes && (
            <div className="mt-3 pt-3 border-t border-gray-300">
              <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
                <span className="text-xs font-medium text-yellow-800 block mb-1">📝 Notizen:</span>
                <span className="text-sm text-yellow-700">{entry.notes}</span>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Extended Employee Management Tab Component for Developer Dashboard
const ExtendedEmployeeManagementTab = ({ employees, allDepartments, getAllOtherDepartments, onEmployeeUpdate, setSelectedEmployee, setShowEmployeeProfile }) => {
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [editingName, setEditingName] = useState('');
  
  // Gruppiere Mitarbeiter nach Wachabteilungen
  const employeesByDepartment = employees.reduce((acc, employee) => {
    const deptName = employee.department_name || 'Unbekannte Abteilung';
    if (!acc[deptName]) {
      acc[deptName] = [];
    }
    acc[deptName].push(employee);
    return acc;
  }, {});

  // Sortiere Abteilungen alphabetisch
  const sortedDepartments = Object.keys(employeesByDepartment).sort();

  const handleEditName = (employee) => {
    setEditingEmployeeId(employee.id);
    setEditingName(employee.name);
  };

  const handleSaveName = async (employeeId) => {
    if (!editingName.trim()) {
      alert('Name darf nicht leer sein');
      return;
    }
    
    try {
      await axios.put(`${API}/developer/employees/${employeeId}/name?name=${encodeURIComponent(editingName.trim())}`);
      setEditingEmployeeId(null);
      setEditingName('');
      onEmployeeUpdate();
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Namens:', error);
      alert('Fehler beim Aktualisieren des Namens');
    }
  };

  const handleCancelEdit = () => {
    setEditingEmployeeId(null);
    setEditingName('');
  };

  const handleMoveEmployee = async (employee, targetDepartmentId) => {
    if (window.confirm(`Mitarbeiter ${employee.name} zur Abteilung verschieben?`)) {
      try {
        await axios.put(`${API}/developer/move-employee/${employee.id}`, {
          new_department_id: targetDepartmentId
        });
        alert('Mitarbeiter erfolgreich verschoben');
        onEmployeeUpdate();
      } catch (error) {
        console.error('Fehler beim Verschieben:', error);
        alert('Fehler beim Verschieben des Mitarbeiters');
      }
    }
  };

  const handleToggleGuestStatus = async (employee) => {
    const newStatus = !employee.is_guest;
    const action = newStatus
      ? `Mitarbeiter "${employee.name}" als Gast markieren?`
      : `Gast-Status von "${employee.name}" entfernen (wird zu normalem Mitarbeiter)?`;
    if (!window.confirm(action)) return;
    try {
      await axios.put(`${API}/developer/employees/${employee.id}/guest-status?is_guest=${newStatus}`);
      onEmployeeUpdate();
    } catch (error) {
      console.error('Fehler beim Ändern des Gast-Status:', error);
      alert('Fehler beim Ändern des Gast-Status');
    }
  };

  const handleViewEmployeeProfile = (employee) => {
    setSelectedEmployee(employee);
    setShowEmployeeProfile(true);
  };

  // getAllOtherDepartments is now passed as a prop from parent

  return (
    <div className="space-y-8">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-2">🔧 Erweiterte Mitarbeiterverwaltung</h2>
        <p className="text-gray-600">Alle Mitarbeiter aus allen Wachabteilungen - Verschieben, Verlauf einsehen und erweiterte Verwaltung</p>
      </div>

      {sortedDepartments.map((departmentName) => {
        const deptEmployees = employeesByDepartment[departmentName];
        
        return (
          <div key={departmentName} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* Abteilungsheader */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-bold text-gray-800">{departmentName}</h3>
                <p className="text-sm text-gray-600">{deptEmployees.length} Mitarbeiter</p>
              </div>
            </div>

            {/* Mitarbeiter-Liste */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {deptEmployees.map((employee) => (
                <div key={employee.id} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      {editingEmployeeId === employee.id ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            className="w-full px-2 py-1 border border-blue-500 rounded text-sm font-semibold"
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveName(employee.id)}
                              className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                            >
                              ✓ Speichern
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
                            >
                              ✗ Abbrechen
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-800">{employee.name}</h4>
                            <button
                              onClick={() => handleEditName(employee)}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                              title="Namen bearbeiten"
                            >
                              ✏️
                            </button>
                          </div>
                          <p className="text-sm text-gray-600">{employee.department_name}</p>
                          {employee.is_guest && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full mt-1 inline-block">👤 Gast</span>
                          )}
                          {employee.is_8h_service && (
                            <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full mt-1 inline-block">🕐 8H</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    <button
                      onClick={() => handleViewEmployeeProfile(employee)}
                      className="w-full bg-blue-600 text-white py-2 px-3 rounded text-sm hover:bg-blue-700 transition-colors"
                    >
                      📋 Vollständiger Verlauf
                    </button>
                    
                    {/* Move Employee Dropdown */}
                    <select
                      onChange={(e) => {
                        if (e.target.value !== '' && e.target.value !== employee.department_id) {
                          handleMoveEmployee(employee, e.target.value);
                        }
                        e.target.selectedIndex = 0; // Reset to first option
                      }}
                      defaultValue=""
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50"
                      data-testid={`move-employee-select-${employee.id}`}
                    >
                      <option value="">🔄 Verschieben nach...</option>
                      {getAllOtherDepartments(employee.department_id).map(dept => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </select>

                    {/* Toggle Guest Status */}
                    <label
                      className="flex items-center justify-between gap-2 w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 cursor-pointer"
                      title="Gast-Status wechseln — Gäste werden im Mitarbeiter-Dashboard separat gruppiert und können vom Sponsoring ausgeschlossen werden."
                    >
                      <span className="text-gray-700">👤 Als Gast markieren</span>
                      <input
                        type="checkbox"
                        checked={!!employee.is_guest}
                        onChange={() => handleToggleGuestStatus(employee)}
                        className="w-4 h-4 accent-blue-600 cursor-pointer"
                        data-testid={`toggle-guest-${employee.id}`}
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Department Management Tab Component for Developer Dashboard
export const DepartmentManagementTab = ({ setSuccessMessage, setShowSuccessNotification }) => {
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/developer/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Abteilungen:', error);
      alert('Fehler beim Laden der Abteilungskonfigurationen');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMergeAccounts = async (departmentId, departmentName) => {
    const confirmText = `⚠️ WARNUNG: Konten zusammenführen für "${departmentName}"\n\nDiese Aktion ist NICHT rückgängig zu machen!\n\nEs werden:\n- Frühstück/Mittag Saldo + Getränke/Snacks Saldo → Ein gemeinsames Konto\n- Alle Mitarbeiter und Subkonten dieser Abteilung betroffen\n- Alte Bestellungen bleiben erhalten\n\nMöchten Sie fortfahren?`;
    
    if (!window.confirm(confirmText)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/developer/merge-accounts/${departmentId}`);
      setSuccessMessage(`✅ Konten erfolgreich zusammengeführt!\n\n${response.data.main_accounts_merged} Hauptkonten und ${response.data.subaccounts_merged} Subkonten wurden zusammengeführt.`);
      setShowSuccessNotification(true);
      
      // Reload departments
      await fetchDepartments();
      
      setTimeout(() => setShowSuccessNotification(false), 5000);
    } catch (error) {
      console.error('Fehler beim Zusammenführen:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Zusammenführen der Konten';
      alert(`❌ Fehler: ${errorMsg}`);
    }
  };

  const handleUpdateConfig = async (departmentId, departmentName, field, newValue) => {
    const fieldNames = {
      'use_combined_accounts': 'Konten-Modus',
      'is_breakfast_lunch_enabled': 'Frühstück & Mittagessen',
      'is_drinks_enabled': 'Getränke',
      'is_snacks_enabled': 'Snacks'
    };
    
    const confirmText = `Möchten Sie "${fieldNames[field]}" für "${departmentName}" ${newValue ? 'aktivieren' : 'deaktivieren'}?`;
    
    if (!window.confirm(confirmText)) {
      return;
    }

    try {
      await axios.put(`${API}/developer/department-config/${departmentId}`, {
        [field]: newValue
      });
      
      setSuccessMessage(`✅ Konfiguration erfolgreich aktualisiert!`);
      setShowSuccessNotification(true);
      
      // Reload departments
      await fetchDepartments();
      
      setTimeout(() => setShowSuccessNotification(false), 3000);
    } catch (error) {
      console.error('Fehler beim Aktualisieren:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Aktualisieren der Konfiguration';
      alert(`❌ Fehler: ${errorMsg}`);
    }
  };

  const handlePaymentAccessOverride = async (departmentId, departmentName, mode) => {
    try {
      await axios.put(
        `${API}/developer/departments/${departmentId}/payment-access-override`,
        {mode}
      );
      await fetchDepartments();
      setSuccessMessage(`Zahlungszugang für ${departmentName} erfolgreich aktualisiert`);
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 3000);
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Zahlungszugangs:', error);
      const errorMsg = error.response?.data?.detail ||
        'Fehler beim Aktualisieren des Zahlungszugangs';
      alert(`❌ ${errorMsg}`);
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Lädt Abteilungskonfigurationen...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-2">⚙️ Wachabteilung verwalten</h2>
        <p className="text-gray-600">Konten-System und Kategorien für jede Wachabteilung konfigurieren</p>
      </div>

      {departments.map((dept) => (
        <div key={dept.department_id} className="bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200">
          <div className="bg-blue-600 text-white px-6 py-4">
            <h3 className="text-lg font-bold">{dept.department_name}</h3>
            <p className="text-sm text-blue-100">ID: {dept.department_id}</p>
          </div>

          <div className="p-6 space-y-6">
            <PaymentAccessOverrideControl
              department={dept}
              onChange={mode => handlePaymentAccessOverride(
                dept.department_id,
                dept.department_name,
                mode
              )}
            />

            {/* Konten-System Konfiguration */}
            <div className="border-b pb-6">
              <h4 className="text-md font-semibold text-gray-800 mb-4">💰 Konten-System</h4>
              
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id={`separate-${dept.department_id}`}
                        name={`accounts-${dept.department_id}`}
                        checked={!dept.use_combined_accounts}
                        onChange={() => handleUpdateConfig(dept.department_id, dept.department_name, 'use_combined_accounts', false)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <label htmlFor={`separate-${dept.department_id}`} className="font-medium text-gray-700">
                        Getrennte Konten (Standard)
                      </label>
                    </div>
                    <p className="text-sm text-gray-500 ml-6">Frühstück/Mittag + Getränke/Snacks = 2 separate Konten</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id={`combined-${dept.department_id}`}
                        name={`accounts-${dept.department_id}`}
                        checked={dept.use_combined_accounts}
                        onChange={() => handleUpdateConfig(dept.department_id, dept.department_name, 'use_combined_accounts', true)}
                        className="w-4 h-4 text-blue-600"
                        disabled={!dept.use_combined_accounts}
                      />
                      <label htmlFor={`combined-${dept.department_id}`} className={`font-medium ${dept.use_combined_accounts ? 'text-gray-700' : 'text-gray-400'}`}>
                        Ein gemeinsames Konto
                      </label>
                      {dept.use_combined_accounts && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Aktiv</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 ml-6">Alles auf einem Konto (Frühstück, Mittag, Getränke, Snacks)</p>
                  </div>
                </div>

                {!dept.use_combined_accounts && (
                  <button
                    onClick={() => handleMergeAccounts(dept.department_id, dept.department_name)}
                    className="mt-2 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors text-sm font-medium"
                  >
                    🔄 Konten jetzt zusammenführen
                  </button>
                )}
              </div>
            </div>

            {/* Sichtbarkeit auf Startseite */}
            <div className="border-b pb-6">
              <h4 className="text-md font-semibold text-gray-800 mb-4">👁️ Sichtbarkeit auf Startseite</h4>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id={`visible-${dept.department_id}`}
                    checked={dept.visible_on_homepage === true}
                    onChange={async (e) => {
                      try {
                        const newVisibility = e.target.checked;
                        await axios.put(
                          `${process.env.REACT_APP_BACKEND_URL || ''}/api/department-settings/${dept.department_id}/homepage-visibility?visible=${newVisibility}`
                        );
                        
                        // Reload departments from server to ensure UI is in sync
                        await fetchDepartments();
                        
                        setSuccessMessage(`Sichtbarkeit erfolgreich ${newVisibility ? 'aktiviert' : 'deaktiviert'}`);
                        setShowSuccessNotification(true);
                        setTimeout(() => setShowSuccessNotification(false), 3000);
                      } catch (error) {
                        console.error('Fehler beim Aktualisieren der Sichtbarkeit:', error);
                        alert('Fehler beim Aktualisieren der Sichtbarkeit');
                      }
                    }}
                    className="w-5 h-5 text-blue-600 rounded"
                  />
                  <label htmlFor={`visible-${dept.department_id}`} className="font-medium text-gray-700">
                    Kachel auf Startseite anzeigen
                  </label>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${dept.visible_on_homepage !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {dept.visible_on_homepage !== false ? 'Sichtbar' : 'Ausgeblendet'}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-2">💡 Nützlich, wenn nicht alle Wachabteilungen die App nutzen. Gilt für ALLE Benutzer.</p>
            </div>

            {/* Kategorien-Aktivierung */}
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-4">📋 Kategorien aktivieren/deaktivieren</h4>
                <p className="text-sm text-gray-600 mb-4">Diese Bereiche können unabhängig vom Konten-System ein- und ausgeblendet werden.</p>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id={`breakfast-${dept.department_id}`}
                        checked={dept.is_breakfast_lunch_enabled}
                        onChange={(e) => handleUpdateConfig(dept.department_id, dept.department_name, 'is_breakfast_lunch_enabled', e.target.checked)}
                        className="w-5 h-5 text-blue-600 rounded"
                      />
                      <label htmlFor={`breakfast-${dept.department_id}`} className="font-medium text-gray-700">
                        🥐 Frühstück & Mittagessen
                      </label>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${dept.is_breakfast_lunch_enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {dept.is_breakfast_lunch_enabled ? 'Aktiviert' : 'Deaktiviert'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id={`drinks-${dept.department_id}`}
                        checked={dept.is_drinks_enabled}
                        onChange={(e) => handleUpdateConfig(dept.department_id, dept.department_name, 'is_drinks_enabled', e.target.checked)}
                        className="w-5 h-5 text-blue-600 rounded"
                      />
                      <label htmlFor={`drinks-${dept.department_id}`} className="font-medium text-gray-700">
                        ☕ Getränke
                      </label>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${dept.is_drinks_enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {dept.is_drinks_enabled ? 'Aktiviert' : 'Deaktiviert'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id={`snacks-${dept.department_id}`}
                        checked={dept.is_snacks_enabled}
                        onChange={(e) => handleUpdateConfig(dept.department_id, dept.department_name, 'is_snacks_enabled', e.target.checked)}
                        className="w-5 h-5 text-blue-600 rounded"
                      />
                      <label htmlFor={`snacks-${dept.department_id}`} className="font-medium text-gray-700">
                        🍫 Snacks
                      </label>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${dept.is_snacks_enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {dept.is_snacks_enabled ? 'Aktiviert' : 'Deaktiviert'}
                    </span>
                  </div>
                </div>
              </div>

          </div>
        </div>
      ))}
    </div>
  );
};

// Developer Employee Profile Component with Extended Functionality
const DeveloperEmployeeProfile = ({ employee, onClose, onRefresh }) => {
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Berlin'
    });
  };

  useEffect(() => {
    fetchEmployeeProfile();
  }, [employee.id]);

  const fetchEmployeeProfile = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/employees/${employee.id}/profile`);
      setEmployeeProfile(response.data);
    } catch (error) {
      console.error('Fehler beim Laden des Mitarbeiterprofils:', error);
      alert('Fehler beim Laden des Profils');
    } finally {
      setIsLoading(false);
    }
  };

  // SALDO-NEUTRAL - Löscht nur Einträge, verändert Salden NICHT
  const deleteHistoryEntry = async (entryId, entryType) => {
    if (window.confirm('Eintrag aus Verlauf löschen? (Salden bleiben unverändert)')) {
      try {
        await axios.delete(`${API}/developer/delete-history-entry/${entryId}?entry_type=${entryType}&employee_id=${employee.id}`);
        alert('Eintrag erfolgreich gelöscht (saldo-neutral)');
        fetchEmployeeProfile(); // Refresh profile
        onRefresh(); // Refresh parent employee list
      } catch (error) {
        console.error('Fehler beim Löschen des Eintrags:', error);
        alert('Fehler beim Löschen des Eintrags');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Lade Mitarbeiterprofil...</p>
        </div>
      </div>
    );
  }

  if (!employeeProfile) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 py-8 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">🔧 {employee.name} - Developer Verlauf</h2>
              <p className="text-gray-600">{employee.department_name}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ×
            </button>
          </div>
        </div>
        
        <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-6">
          {/* Balance Overview */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className={`text-2xl font-bold ${parseFloat(employeeProfile.employee?.breakfast_balance || employeeProfile.breakfast_total || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {parseFloat(employeeProfile.employee?.breakfast_balance || employeeProfile.breakfast_total || 0).toFixed(2)}€
              </div>
              <div className="text-gray-600">Frühstück/Mittag</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <div className={`text-2xl font-bold ${parseFloat(employeeProfile.employee?.drinks_sweets_balance || employeeProfile.drinks_sweets_total || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {parseFloat(employeeProfile.employee?.drinks_sweets_balance || employeeProfile.drinks_sweets_total || 0).toFixed(2)}€
              </div>
              <div className="text-gray-600">Getränke/Snacks</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <div className={`text-2xl font-bold ${(parseFloat(employeeProfile.employee?.breakfast_balance || employeeProfile.breakfast_total || 0) + parseFloat(employeeProfile.employee?.drinks_sweets_balance || employeeProfile.drinks_sweets_total || 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {(parseFloat(employeeProfile.employee?.breakfast_balance || employeeProfile.breakfast_total || 0) + parseFloat(employeeProfile.employee?.drinks_sweets_balance || employeeProfile.drinks_sweets_total || 0)).toFixed(2)}€
              </div>
              <div className="text-gray-600">Gesamt</div>
            </div>
          </div>

          {/* Order History with Developer Controls */}
          <div>
            <h3 className="text-xl font-semibold mb-4">🔧 Vollständiger Verlauf (Developer Controls)</h3>
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
              <p className="text-yellow-800 text-sm">
                ⚠️ <strong>Saldo-neutrale Löschungen:</strong> Einträge werden nur aus dem Verlauf entfernt, Salden bleiben unverändert!
              </p>
            </div>
            
            <HistoryDisplay 
              employeeProfile={employeeProfile} 
              formatDate={formatDate}
              deleteHistoryEntry={deleteHistoryEntry}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// General Settings Tab Component
const GeneralSettingsTab = () => {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('🔧 Wartungsarbeiten werden durchgeführt. Bitte haben Sie etwas Geduld.');
  const [prelaunchMode, setPrelaunchMode] = useState(false);
  const [prelaunchHeadline, setPrelaunchHeadline] = useState('FW Kantine für deine Wache');
  const [prelaunchMessage, setPrelaunchMessage] = useState(
    'Diese Seite ist bereits für deine Wache vorbereitet. Möchtet ihr FW Kantine nutzen? Informiert euch über alle Funktionen oder nehmt direkt Kontakt auf.'
  );
  const [prelaunchWebsite, setPrelaunchWebsite] = useState('https://fw-kantine.de');
  const [prelaunchEmail, setPrelaunchEmail] = useState('info@creativey.media');
  const [prelaunchWhatsapp, setPrelaunchWhatsapp] = useState('');
  const [prelaunchWhatsappMessage, setPrelaunchWhatsappMessage] = useState(
    'Hallo, wir interessieren uns für FW Kantine und möchten mehr erfahren.'
  );
  const [isSavingOperatingMode, setIsSavingOperatingMode] = useState(false);
  const [isSavingPrelaunch, setIsSavingPrelaunch] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Announcement Banner States
  const [announcementActive, setAnnouncementActive] = useState(false);
  const [announcementMessage, setAnnouncementMessage] = useState('');
  const [announcementColor, setAnnouncementColor] = useState('blue');
  const [isSavingAnnouncement, setIsSavingAnnouncement] = useState(false);
  
  // App Settings State
  const [wacheNummer, setWacheNummer] = useState('4');
  const [canEditWacheNummer, setCanEditWacheNummer] = useState(true);
  const [appTitle, setAppTitle] = useState('');
  const [currentLogo, setCurrentLogo] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [isSavingLogo, setIsSavingLogo] = useState(false);
  
  // Department Reset States
  const [departments, setDepartments] = useState([]);
  const [eightHEmployees, setEightHEmployees] = useState([]);
  const [resetType, setResetType] = useState('department'); // 'department' or '8h'
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selected8HEmployee, setSelected8HEmployee] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  
  // Breakfast Lock States
  const [lockedDepartments, setLockedDepartments] = useState([]);
  const [isLoadingLocks, setIsLoadingLocks] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  
  // Sponsoring Reset States
  const [sponsoringResetDept, setSponsoringResetDept] = useState('');
  const [sponsoringResetDate, setSponsoringResetDate] = useState(new Date().toISOString().split('T')[0]);
  const [isResettingSponsoring, setIsResettingSponsoring] = useState(false);

  useEffect(() => {
    fetchMaintenanceMode();
    fetchPrelaunchMode();
    fetchAnnouncement();
    fetchDepartments();
    fetch8HEmployees();
    fetchLockedDepartments();
    fetchAppSettings();
  }, []);

  const decodeEmailToken = (token) => {
    if (!token) return '';
    try {
      return window.atob(token.replace(/-/g, '+').replace(/_/g, '/'));
    } catch (error) {
      console.error('Error decoding contact email:', error);
      return '';
    }
  };

  const applyPrelaunchResponse = (data) => {
    setPrelaunchMode(data.is_active);
    setPrelaunchHeadline(data.headline || 'FW Kantine für deine Wache');
    setPrelaunchMessage(data.message || '');
    setPrelaunchWebsite(data.website_url || 'https://fw-kantine.de');
    setPrelaunchEmail(decodeEmailToken(data.email_token) || '');
    setPrelaunchWhatsapp(data.whatsapp_number || '');
    setPrelaunchWhatsappMessage(data.whatsapp_message || '');
  };

  const fetchPrelaunchMode = async () => {
    try {
      const response = await axios.get(`${API}/prelaunch-mode`);
      applyPrelaunchResponse(response.data);
    } catch (error) {
      console.error('Error fetching prelaunch mode:', error);
    }
  };
  
  const fetchAnnouncement = async () => {
    try {
      const response = await axios.get(`${API}/announcement`);
      setAnnouncementActive(response.data.is_active);
      setAnnouncementMessage(response.data.message || '');
      setAnnouncementColor(response.data.color || 'blue');
    } catch (error) {
      console.error('Error fetching announcement:', error);
    }
  };
  
  const handleSaveAnnouncement = async () => {
    try {
      setIsSavingAnnouncement(true);
      const response = await axios.post(`${API}/developer/announcement`, {
        is_active: announcementActive,
        message: announcementMessage,
        color: announcementColor
      });
      
      alert(response.data.is_active 
        ? '✅ Ankündigung aktiviert!' 
        : '✅ Ankündigung deaktiviert!'
      );
    } catch (error) {
      console.error('Error saving announcement:', error);
      alert('❌ Fehler beim Speichern der Ankündigung');
    } finally {
      setIsSavingAnnouncement(false);
    }
  };
  
  const fetchAppSettings = async () => {
    try {
      const response = await axios.get(`${API}/app-settings`);
      setWacheNummer(response.data.wache_nummer);
      setCanEditWacheNummer(response.data.can_edit_wache_nummer);
      setAppTitle(response.data.title || '');
      setCurrentLogo(response.data.logo_base64 || null);
    } catch (error) {
      console.error('Error fetching app settings:', error);
    }
  };
  
  // Save App Title
  const saveAppTitle = async () => {
    if (!appTitle.trim()) {
      alert('Bitte geben Sie einen Titel ein');
      return;
    }
    
    try {
      setIsSavingTitle(true);
      await axios.post(`${API}/developer/app-settings`, {
        title: appTitle.trim()
      });
      alert('✅ Titel erfolgreich gespeichert!');
      // Regenerate config.js
      await axios.post(`${API}/developer/regenerate-config`);
    } catch (error) {
      console.error('Error saving title:', error);
      alert('❌ Fehler beim Speichern: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSavingTitle(false);
    }
  };

  // Handle Logo File Select
  const handleLogoFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Bitte wählen Sie eine Bilddatei (PNG, JPG, etc.)');
      return;
    }
    
    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Die Datei ist zu groß. Maximale Größe: 2MB');
      return;
    }
    
    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Upload Logo
  const handleLogoUpload = async () => {
    if (!logoFile) {
      alert('Bitte wählen Sie zuerst eine Datei aus');
      return;
    }
    
    try {
      setIsSavingLogo(true);
      
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result;
        
        try {
          await axios.post(`${API}/developer/app-settings`, {
            logo_base64: base64
          });
          
          setCurrentLogo(base64);
          setLogoFile(null);
          setLogoPreview(null);
          
          // Regenerate config.js
          await axios.post(`${API}/developer/regenerate-config`);
          
          alert('✅ Logo erfolgreich hochgeladen!');
        } catch (error) {
          console.error('Error uploading logo:', error);
          alert('❌ Fehler beim Hochladen: ' + (error.response?.data?.detail || error.message));
        } finally {
          setIsSavingLogo(false);
        }
      };
      reader.readAsDataURL(logoFile);
    } catch (error) {
      console.error('Error reading file:', error);
      setIsSavingLogo(false);
    }
  };
  
  const saveWacheNummer = async () => {
    if (!canEditWacheNummer) {
      alert('⚠️ Wachnummer kann nicht geändert werden!\n\nWachabteilungen existieren bereits. Eine Änderung würde alle Zuordnungen zerstören.');
      return;
    }
    
    if (!wacheNummer || wacheNummer.trim() === '') {
      alert('❌ Bitte geben Sie eine Wachnummer ein (z.B. 4600)');
      return;
    }
    
    const confirm = window.confirm(
      `⚠️ ACHTUNG: Wachnummer ändern?\n\n` +
      `Neue Department-IDs werden sein:\n` +
      `- fw${wacheNummer}abteilung1\n` +
      `- fw${wacheNummer}abteilung2\n` +
      `- fw${wacheNummer}abteilung3\n` +
      `- fw${wacheNummer}abteilung4\n\n` +
      `Dies kann NUR JETZT geändert werden, bevor Departments angelegt werden!\n\n` +
      `Fortfahren?`
    );
    
    if (!confirm) return;
    
    try {
      await axios.post(`${API}/developer/app-settings`, {
        wache_nummer: wacheNummer
      });
      alert('✅ Wachnummer erfolgreich gespeichert!\n\nBitte legen Sie jetzt die Wachabteilungen mit "Init Datenbank" an.');
      fetchAppSettings(); // Refresh to update canEdit status
    } catch (error) {
      console.error('Error saving wache nummer:', error);
      alert('❌ ' + (error.response?.data?.detail || 'Fehler beim Speichern der Wachnummer'));
    }
  };
  
  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };
  
  const fetch8HEmployees = async () => {
    try {
      // Get all departments first
      const deptResponse = await axios.get(`${API}/departments`);
      const departments = deptResponse.data;
      
      // Get 8H employees from each department
      const allEmployees = [];
      for (const dept of departments) {
        try {
          const empResponse = await axios.get(`${API}/departments/${dept.id}/8h-employees`);
          allEmployees.push(...empResponse.data);
        } catch (error) {
          console.error(`Error fetching 8H employees for ${dept.id}:`, error);
        }
      }
      
      // Remove duplicates (8H employees appear in all departments)
      const uniqueEmployees = Array.from(new Map(allEmployees.map(emp => [emp.id, emp])).values());
      setEightHEmployees(uniqueEmployees);
    } catch (error) {
      console.error('Error fetching 8H employees:', error);
    }
  };

  const fetchMaintenanceMode = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/maintenance-mode`);
      setMaintenanceMode(response.data.is_maintenance_mode);
      setMaintenanceMessage(response.data.message || '🔧 Wartungsarbeiten werden durchgeführt. Bitte haben Sie etwas Geduld.');
      setLastUpdated(response.data.last_updated);
    } catch (error) {
      console.error('Error fetching maintenance mode:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const savePrelaunchSettings = async (active = prelaunchMode, showConfirmation = true) => {
    try {
      setIsSavingPrelaunch(true);
      const response = await axios.post(`${API}/developer/prelaunch-mode`, {
        is_active: active,
        headline: prelaunchHeadline,
        message: prelaunchMessage,
        website_url: prelaunchWebsite,
        contact_email: prelaunchEmail || null,
        whatsapp_number: prelaunchWhatsapp || null,
        whatsapp_message: prelaunchWhatsappMessage
      });
      applyPrelaunchResponse(response.data);
      if (active) {
        setMaintenanceMode(false);
      }
      if (showConfirmation) {
        alert('✅ Interessentenseite erfolgreich gespeichert.');
      }
      return true;
    } catch (error) {
      console.error('Error saving prelaunch mode:', error);
      alert(
        '❌ Fehler beim Speichern: ' +
        (error.response?.data?.detail || error.message)
      );
      return false;
    } finally {
      setIsSavingPrelaunch(false);
    }
  };

  const setOperatingMode = async (mode) => {
    if (isSavingOperatingMode) return;

    try {
      setIsSavingOperatingMode(true);

      if (mode === 'prelaunch') {
        const saved = await savePrelaunchSettings(true, false);
        if (saved) {
          setMaintenanceMode(false);
          alert('✅ Interessentenmodus aktiviert.');
        }
        return;
      }

      if (mode === 'maintenance') {
        const response = await axios.post(`${API}/developer/maintenance-mode`, {
          is_active: true,
          message: maintenanceMessage
        });
        setMaintenanceMode(response.data.is_maintenance_mode);
        setPrelaunchMode(false);
        setLastUpdated(response.data.last_updated);
        alert('✅ Wartungsmodus aktiviert.');
        return;
      }

      if (maintenanceMode) {
        await axios.post(`${API}/developer/maintenance-mode`, {
          is_active: false,
          message: maintenanceMessage
        });
      }
      if (prelaunchMode) {
        await savePrelaunchSettings(false, false);
      }
      setMaintenanceMode(false);
      setPrelaunchMode(false);
      alert('✅ Normalbetrieb aktiviert.');
    } catch (error) {
      console.error('Error changing operating mode:', error);
      alert(
        '❌ Betriebsstatus konnte nicht geändert werden: ' +
        (error.response?.data?.detail || error.message)
      );
    } finally {
      setIsSavingOperatingMode(false);
    }
  };
  
  // Logo Upload Handlers
  // handleLogoFileSelect and handleLogoUpload removed - logo is now static in /logo.png
  
  const handleResetDepartment = () => {
    if (resetType === 'department' && !selectedDepartment) {
      alert('❌ Bitte wählen Sie eine Wachabteilung aus!');
      return;
    }
    if (resetType === '8h' && !selected8HEmployee) {
      alert('❌ Bitte wählen Sie einen 8H-Mitarbeiter aus!');
      return;
    }
    setShowResetConfirmation(true);
  };
  
  const confirmResetDepartment = async () => {
    if (confirmationText !== 'Löschen') {
      alert('❌ Bestätigungstext ungültig! Bitte geben Sie "Löschen" ein.');
      return;
    }
    
    try {
      setIsResetting(true);
      
      let response;
      if (resetType === 'department') {
        response = await axios.post(`${API}/developer/reset-department`, {
          department_id: selectedDepartment,
          confirmation_text: confirmationText
        });
      } else {
        response = await axios.post(`${API}/developer/reset-8h-employee`, {
          employee_id: selected8HEmployee,
          confirmation_text: confirmationText
        });
      }
      
      const result = response.data;
      
      if (resetType === 'department') {
        alert(
          `✅ ${result.message}\n\n` +
          `📊 Details:\n` +
          `• Bestellungen gelöscht: ${result.orders_deleted}\n` +
          `• Mitarbeiter zurückgesetzt: ${result.employees_reset}\n` +
          `• Subkonten zurückgesetzt: ${result.subaccounts_reset}\n` +
          `• Zahlungsprotokolle gelöscht: ${result.payment_logs_deleted}`
        );
      } else {
        alert(
          `✅ ${result.message}\n\n` +
          `📊 Details:\n` +
          `• Bestellungen gelöscht: ${result.orders_deleted}\n` +
          `• Subkonten zurückgesetzt: ${result.subaccounts_reset}\n` +
          `• Zahlungsprotokolle gelöscht: ${result.payment_logs_deleted}`
        );
      }
      
      // Reset form
      setSelectedDepartment('');
      setSelected8HEmployee('');
      setConfirmationText('');
      setShowResetConfirmation(false);
      setResetType('department');
    } catch (error) {
      console.error('Error resetting:', error);
      alert(`❌ Fehler beim Zurücksetzen: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsResetting(false);
    }
  };
  
  const fetchLockedDepartments = async () => {
    try {
      setIsLoadingLocks(true);
      const response = await axios.get(`${API}/developer/locked-breakfast-departments`);
      setLockedDepartments(response.data);
    } catch (error) {
      console.error('Error fetching locked departments:', error);
    } finally {
      setIsLoadingLocks(false);
    }
  };
  
  const handleUnlockBreakfast = async (departmentId, departmentName) => {
    if (!window.confirm(`Möchten Sie die Frühstücks-Sperre für ${departmentName} wirklich aufheben?`)) {
      return;
    }
    
    try {
      setIsUnlocking(true);
      const response = await axios.post(`${API}/developer/unlock-breakfast/${departmentId}`);
      alert(response.data.message);
      
      // Refresh locked departments list
      await fetchLockedDepartments();
    } catch (error) {
      console.error('Error unlocking breakfast:', error);
      alert(`❌ Fehler beim Entsperren: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsUnlocking(false);
    }
  };
  
  const handleResetSponsoring = async () => {
    if (!sponsoringResetDept) {
      alert('❌ Bitte wählen Sie eine Wachabteilung aus!');
      return;
    }
    
    if (!sponsoringResetDate) {
      alert('❌ Bitte wählen Sie ein Datum aus!');
      return;
    }
    
    const deptName = departments.find(d => d.id === sponsoringResetDept)?.name || sponsoringResetDept;
    
    if (!window.confirm(
      `⚠️ ACHTUNG: Sponsoring zurücksetzen?\n\n` +
      `Wachabteilung: ${deptName}\n` +
      `Datum: ${sponsoringResetDate}\n\n` +
      `Dies wird:\n` +
      `• Die gespeicherten Sponsoring-Gutschriften zurücknehmen\n` +
      `• Den Sponsor um die ausgegebenen Beträge entlasten\n` +
      `• Die zugehörigen Sponsoring-Markierungen entfernen\n` +
      `• Nur die zugehörigen Sponsoring-Sperren aufheben\n` +
      `• Das Frühstück kann danach erneut ausgegeben werden\n\n` +
      `Fortfahren?`
    )) {
      return;
    }
    
    try {
      setIsResettingSponsoring(true);
      const response = await axios.post(`${API}/developer/reset-sponsoring/${sponsoringResetDept}/${sponsoringResetDate}`);
      
      alert(
        `${response.data.message}\n\n` +
        `📊 Details:\n` +
        `• Bestellungen zurückgesetzt: ${response.data.reset_counts.orders_reset}\n` +
        `• Sponsoring-Einstellungen: ${response.data.reset_counts.sponsoring_settings_reset}\n` +
        `• Frühstücks-Einstellungen: ${response.data.reset_counts.breakfast_settings_reset}`
      );
      
      // Refresh locked departments list
      await fetchLockedDepartments();
    } catch (error) {
      console.error('Error resetting sponsoring:', error);
      alert(`❌ Fehler beim Zurücksetzen: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsResettingSponsoring(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-600 mt-4">Lade Einstellungen...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-semibold mb-4">⚙️ Allgemeine Einstellungen</h3>

        {/* Operating Mode Section */}
        <div className="border-t pt-6 mt-6">
          <h4 className="text-lg font-semibold mb-2">Betriebsstatus</h4>
          <p className="text-sm text-gray-600 mb-5">
            Legt fest, was Besucher dieser Wache sehen. Der Developer-Zugang bleibt in jedem Status erreichbar.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              {
                id: 'normal',
                title: 'Normalbetrieb',
                description: 'Die Kantinen-App ist vollständig erreichbar.',
                active: !maintenanceMode && !prelaunchMode,
                activeClass: 'border-emerald-500 bg-emerald-50 text-emerald-900'
              },
              {
                id: 'prelaunch',
                title: 'Interessentenmodus',
                description: 'Zeigt die eigenständige Werbe- und Kontaktseite.',
                active: prelaunchMode,
                activeClass: 'border-sky-500 bg-sky-50 text-sky-900'
              },
              {
                id: 'maintenance',
                title: 'Wartungsmodus',
                description: 'Sperrt die App vorübergehend mit einem Hinweis.',
                active: maintenanceMode,
                activeClass: 'border-amber-500 bg-amber-50 text-amber-900'
              }
            ].map((mode) => (
              <button
                key={mode.id}
                type="button"
                onClick={() => setOperatingMode(mode.id)}
                disabled={isSavingOperatingMode || isSavingPrelaunch}
                className={`rounded-xl border-2 p-4 text-left transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  mode.active
                    ? mode.activeClass
                    : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:bg-gray-50'
                } ${(isSavingOperatingMode || isSavingPrelaunch) ? 'cursor-wait opacity-60' : ''}`}
              >
                <span className="flex items-center justify-between gap-3 font-semibold">
                  {mode.title}
                  <span className={`h-3 w-3 rounded-full ${mode.active ? 'bg-current' : 'bg-gray-300'}`} />
                </span>
                <span className="mt-2 block text-sm leading-5 opacity-75">
                  {mode.description}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Prelaunch Page Configuration */}
        <div className="border-t pt-6 mt-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h4 className="text-lg font-semibold">Interessentenseite</h4>
              <p className="mt-1 text-sm text-gray-600">
                Inhalte und Kontaktwege für vorbereitete, noch nicht vergebene Wachen.
              </p>
            </div>
            {prelaunchMode && (
              <span className="self-start rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-800">
                Aktuell öffentlich sichtbar
              </span>
            )}
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <label className="block lg:col-span-2">
              <span className="text-sm font-medium text-gray-700">Überschrift</span>
              <input
                type="text"
                value={prelaunchHeadline}
                onChange={(event) => setPrelaunchHeadline(event.target.value)}
                maxLength={120}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              />
            </label>

            <label className="block lg:col-span-2">
              <span className="text-sm font-medium text-gray-700">Beschreibung</span>
              <textarea
                value={prelaunchMessage}
                onChange={(event) => setPrelaunchMessage(event.target.value)}
                rows={4}
                maxLength={1200}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">Informationsseite</span>
              <input
                type="url"
                value={prelaunchWebsite}
                onChange={(event) => setPrelaunchWebsite(event.target.value)}
                placeholder="https://fw-kantine.de"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">Kontakt-E-Mail</span>
              <input
                type="email"
                value={prelaunchEmail}
                onChange={(event) => setPrelaunchEmail(event.target.value)}
                placeholder="info@creativey.media"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              />
              <span className="mt-1 block text-xs text-gray-500">
                Wird erst beim Klick für das E-Mail-Programm zusammengesetzt.
              </span>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">WhatsApp-Nummer</span>
              <input
                type="tel"
                value={prelaunchWhatsapp}
                onChange={(event) => setPrelaunchWhatsapp(event.target.value)}
                placeholder="491701234567"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              />
              <span className="mt-1 block text-xs text-gray-500">
                Mit Ländervorwahl, zum Beispiel 49 statt einer führenden 0.
              </span>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">Vorbereitete WhatsApp-Nachricht</span>
              <textarea
                value={prelaunchWhatsappMessage}
                onChange={(event) => setPrelaunchWhatsappMessage(event.target.value)}
                rows={3}
                maxLength={500}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              />
            </label>
          </div>

          <button
            type="button"
            onClick={() => savePrelaunchSettings(prelaunchMode, true)}
            disabled={isSavingPrelaunch}
            className="mt-5 rounded-lg bg-slate-800 px-5 py-2.5 font-medium text-white transition hover:bg-slate-700 disabled:cursor-wait disabled:opacity-60"
          >
            {isSavingPrelaunch ? 'Speichert …' : 'Interessentenseite speichern'}
          </button>
        </div>
        
        {/* Maintenance Mode Section */}
        <div className="border-t pt-6 mt-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <h4 className="text-lg font-semibold mb-2">Wartungshinweis</h4>
              <p className="text-sm text-gray-600 mb-4">
                Dieser Text erscheint ausschließlich, wenn oben der Wartungsmodus ausgewählt wurde.
              </p>
              
              {/* Status Badge */}
              <div className="mb-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  maintenanceMode 
                    ? 'bg-red-100 text-red-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {maintenanceMode ? 'Wartungsmodus aktiv' : 'Wartungsmodus nicht aktiv'}
                </span>
              </div>
              
              {/* Last Updated */}
              {lastUpdated && (
                <p className="text-xs text-gray-500 mb-4">
                  Zuletzt geändert: {new Date(lastUpdated).toLocaleString('de-DE', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: 'Europe/Berlin'
                  })}
                </p>
              )}
              
              {/* Message Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Wartungsnachricht:
                </label>
                <textarea
                  value={maintenanceMessage}
                  onChange={(e) => setMaintenanceMessage(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nachricht für die Benutzer während des Wartungsmodus"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Diese Nachricht wird den Benutzern angezeigt, wenn der Wartungsmodus aktiv ist.
                </p>
                <button
                  type="button"
                  onClick={() => setOperatingMode('maintenance')}
                  disabled={isSavingOperatingMode}
                  className="mt-3 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-wait disabled:opacity-60"
                >
                  {maintenanceMode
                    ? 'Wartungshinweis speichern'
                    : 'Wartungsmodus mit diesem Hinweis aktivieren'}
                </button>
              </div>
            </div>
            
          </div>
          
          {/* Warning Box */}
          {maintenanceMode && (
            <div className="mt-6 bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    <strong>Achtung:</strong> Der Wartungsmodus ist aktiv! Alle normalen Benutzer sind derzeit gesperrt und können die App nicht nutzen.
                  </p>
                </div>
              </div>
            </div>
          )}

        </div>
        
        {/* Announcement Banner Section */}
        <div className="border-t pt-6 mt-6">
          <h4 className="text-lg font-semibold mb-2">📢 Hinweis-Banner</h4>
          <p className="text-sm text-gray-600 mb-4">
            Zeigt ein dezentes Banner oben auf der Seite an. Ideal für Ankündigungen wie geplante Wartungsarbeiten.
          </p>
          
          {/* Preview */}
          {announcementActive && announcementMessage && (
            <div className={`mb-4 p-3 rounded-lg border ${
              announcementColor === 'green' ? 'bg-green-50 border-green-200 text-green-800' :
              announcementColor === 'blue' ? 'bg-blue-50 border-blue-200 text-blue-800' :
              announcementColor === 'yellow' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' :
              'bg-red-50 border-red-200 text-red-800'
            }`}>
              <p className="whitespace-normal break-words [overflow-wrap:anywhere] text-sm font-medium">
                Vorschau: {announcementMessage}
              </p>
            </div>
          )}
          
          {/* Active Toggle */}
          <div className="flex items-center gap-3 mb-4">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={announcementActive}
                onChange={(e) => setAnnouncementActive(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-300 peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
            <span className="text-sm font-medium text-gray-700">
              {announcementActive ? '✅ Aktiv' : '❌ Deaktiviert'}
            </span>
          </div>
          
          {/* Color Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Farbe / Dringlichkeit:</label>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'green', label: '🟢 Info (Grün)', style: 'bg-green-100 text-green-800 border-green-300' },
                { value: 'blue', label: '🔵 Hinweis (Blau)', style: 'bg-blue-100 text-blue-800 border-blue-300' },
                { value: 'yellow', label: '🟡 Warnung (Gelb)', style: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
                { value: 'red', label: '🔴 Wichtig (Rot)', style: 'bg-red-100 text-red-800 border-red-300' }
              ].map(color => (
                <button
                  key={color.value}
                  onClick={() => setAnnouncementColor(color.value)}
                  className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${color.style} ${
                    announcementColor === color.value 
                      ? 'ring-2 ring-offset-2 ring-gray-400 scale-105' 
                      : 'opacity-70 hover:opacity-100'
                  }`}
                >
                  {color.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Message Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Nachricht:</label>
            <input
              type="text"
              value={announcementMessage}
              onChange={(e) => setAnnouncementMessage(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="z.B. Geplante Wartung am 15.01. von 08:00-09:00 Uhr"
              maxLength={200}
            />
            <p className="text-xs text-gray-500 mt-1">{announcementMessage.length}/200 Zeichen</p>
          </div>
          
          {/* Save Button */}
          <button
            onClick={handleSaveAnnouncement}
            disabled={isSavingAnnouncement}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              isSavingAnnouncement
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {isSavingAnnouncement ? '⏳ Speichern...' : '💾 Banner speichern'}
          </button>
        </div>

        {/* App Titel & Logo */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">🎨 App-Titel & Logo</h4>
          <p className="text-sm text-gray-600 mb-4">
            Änderungen werden automatisch für alle Benutzer übernommen (via config.js).
          </p>
          
          {/* Title Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              App-Titel (wird im Browser-Tab und auf der Startseite angezeigt)
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={appTitle}
                onChange={(e) => setAppTitle(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="z.B. Kantine Feuerwache 4600"
              />
              <button
                onClick={saveAppTitle}
                disabled={isSavingTitle}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors whitespace-nowrap"
              >
                {isSavingTitle ? '⏳ Speichern...' : '💾 Titel speichern'}
              </button>
            </div>
          </div>
          
          {/* Logo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo (PNG oder JPG, max. 2MB)
            </label>
            
            <div className="flex flex-col md:flex-row gap-4 items-start">
              {/* Current Logo Preview */}
              <div className="flex-shrink-0">
                <p className="text-xs text-gray-500 mb-2">Aktuelles Logo:</p>
                <div className="w-32 h-32 border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                  {currentLogo ? (
                    <img src={currentLogo} alt="Aktuelles Logo" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <span className="text-gray-400 text-sm">Kein Logo</span>
                  )}
                </div>
              </div>
              
              {/* New Logo Preview */}
              {logoPreview && (
                <div className="flex-shrink-0">
                  <p className="text-xs text-gray-500 mb-2">Neues Logo (Vorschau):</p>
                  <div className="w-32 h-32 border-2 border-green-300 rounded-lg overflow-hidden bg-green-50 flex items-center justify-center">
                    <img src={logoPreview} alt="Neues Logo" className="max-w-full max-h-full object-contain" />
                  </div>
                </div>
              )}
              
              {/* Upload Controls */}
              <div className="flex-1 space-y-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoFileSelect}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {logoFile && (
                  <div className="flex gap-2">
                    <button
                      onClick={handleLogoUpload}
                      disabled={isSavingLogo}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      {isSavingLogo ? '⏳ Hochladen...' : '✅ Logo hochladen'}
                    </button>
                    <button
                      onClick={() => {
                        setLogoFile(null);
                        setLogoPreview(null);
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      ❌ Abbrechen
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <StationModeSettings />
        {/* Wachnummer Configuration - ONLY editable if no departments exist */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">🔢 Wachkennung konfigurieren</h4>
          <p className="text-sm text-gray-600 mb-4">
            Die Wachnummer bestimmt die Department-IDs (z.B. fw<strong>{wacheNummer}</strong>abteilung1).
          </p>
          
          {!canEditWacheNummer && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-700">
                ⚠️ <strong>Gesperrt:</strong> Wachnummer kann nicht mehr geändert werden, da bereits Wachabteilungen existieren.
                Eine Änderung würde alle Mitarbeiter, Bestellungen und Zuordnungen zerstören!
              </p>
            </div>
          )}
          
          {canEditWacheNummer && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-yellow-700">
                💡 <strong>Nur bei Neuinstallation:</strong> Dies kann nur JETZT geändert werden, bevor Sie Wachabteilungen anlegen!
              </p>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={wacheNummer}
              onChange={(e) => setWacheNummer(e.target.value)}
              disabled={!canEditWacheNummer}
              className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !canEditWacheNummer ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
              }`}
              placeholder="z.B. 5430 oder rw25"
            />
            <button
              onClick={saveWacheNummer}
              disabled={!canEditWacheNummer}
              className={`px-6 py-2 rounded-lg transition-colors whitespace-nowrap ${
                !canEditWacheNummer
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              💾 Speichern
            </button>
          </div>
          
          <p className="text-xs text-gray-500 mt-3">
            Nach dem Speichern werden Department-IDs wie fw{wacheNummer}abteilung1, fw{wacheNummer}abteilung2, etc. erstellt.
          </p>
        </div>
        
        {/* Repair Menu Items Section */}
        <div className="bg-yellow-50 border border-yellow-200 p-4 sm:p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">🔧 Menu-Items reparieren</h4>
          <p className="text-sm text-gray-600 mb-4">
            Falls nach dem Setup Brötchen oder Department-Einstellungen fehlen, können Sie diese hier nachträglich hinzufügen.
            Diese Funktion ist sicher und kann mehrfach ausgeführt werden - es werden nur fehlende Items hinzugefügt.
          </p>
          
          <div className="bg-white p-3 sm:p-4 rounded-lg mb-4">
            <p className="text-sm text-gray-700 mb-2"><strong>Was wird hinzugefügt?</strong></p>
            <ul className="text-xs sm:text-sm text-gray-600 space-y-1 list-disc list-inside">
              <li className="break-words">Helle Brötchen (0.50€) - KRITISCH für Frühstücksbestellungen</li>
              <li className="break-words">Körner Brötchen (0.60€) - KRITISCH für Frühstücksbestellungen</li>
              <li className="break-words">Department Settings (Eier-Preis 0.50€, Kaffee-Preis 1.00€)</li>
              <li className="break-words">Globale Mittagessen-Einstellungen (0.00€ - Admin setzt täglich manuell)</li>
            </ul>
          </div>
          
          <button
            onClick={async () => {
              if (!window.confirm('Menu-Items für alle Departments reparieren?\\n\\nFehlende Brötchen und Einstellungen werden hinzugefügt.')) {
                return;
              }
              
              try {
                const response = await axios.post(`${API}/setup/repair-missing-menu-items`);
                alert('✅ ' + response.data.message);
              } catch (error) {
                console.error('Fehler beim Reparieren:', error);
                alert('❌ Fehler beim Reparieren: ' + (error.response?.data?.detail || error.message));
              }
            }}
            className="w-full sm:w-auto px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium"
          >
            🔧 Menu-Items jetzt reparieren
          </button>
        </div>
        
        {/* Cleanup Hardcoded Subaccounts Section */}
        <div className="bg-blue-50 border border-blue-200 p-4 sm:p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">🧹 Hardcoded Subkonten bereinigen</h4>
          <p className="text-sm text-gray-600 mb-4">
            Falls Mitarbeiter doppelte Wachabteilungen angezeigt bekommen (z.B. "1. WA" erscheint zweimal),
            können Sie hier alte hardcoded Subkonten entfernen. Dies betrifft nur ungültige Department-IDs.
          </p>
          
          <div className="bg-white p-3 sm:p-4 rounded-lg mb-4">
            <p className="text-sm text-gray-700 mb-2"><strong>Was wird bereinigt?</strong></p>
            <ul className="text-xs sm:text-sm text-gray-600 space-y-1 list-disc list-inside">
              <li className="break-words">Entfernt fw4abteilung1-4 Subkonten, wenn andere Department-IDs existieren</li>
              <li className="break-words">Behält nur Subkonten für tatsächlich existierende Departments</li>
              <li className="break-words">Sicher: Nur leere oder ungültige Subkonten werden entfernt</li>
            </ul>
          </div>
          
          <button
            onClick={async () => {
              if (!window.confirm('Hardcoded Subkonten für alle Mitarbeiter bereinigen?\\n\\nUngültige Department-Subkonten werden entfernt.')) {
                return;
              }
              
              try {
                const response = await axios.post(`${API}/setup/cleanup-hardcoded-subaccounts`);
                alert('✅ ' + response.data.message);
              } catch (error) {
                console.error('Fehler beim Cleanup:', error);
                alert('❌ Fehler beim Cleanup: ' + (error.response?.data?.detail || error.message));
              }
            }}
            className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            🧹 Subkonten jetzt bereinigen
          </button>
        </div>
        
        {/* Initialize All Subaccounts Section */}
        <div className="bg-green-50 border border-green-200 p-4 sm:p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">🔄 Alle Subkonten initialisieren</h4>
          <p className="text-sm text-gray-600 mb-4">
            Stellt sicher, dass ALLE Mitarbeiter Subkonten für ALLE Departments haben (auch mit 0€).
            Dies behebt das Problem, dass 8H-Mitarbeiter oder Gäste leere Departments nicht sehen.
          </p>
          
          <div className="bg-white p-3 sm:p-4 rounded-lg mb-4">
            <p className="text-sm text-gray-700 mb-2"><strong>Was wird initialisiert?</strong></p>
            <ul className="text-xs sm:text-sm text-gray-600 space-y-1 list-disc list-inside">
              <li className="break-words">Erstellt Subkonten für alle existierenden Departments</li>
              <li className="break-words">Setzt initiale Balances auf 0.00€ wenn noch nicht vorhanden</li>
              <li className="break-words">Überschreibt KEINE existierenden Balances</li>
              <li className="break-words">Ermöglicht vollständige Übersicht: Alle WA angezeigt, auch mit 0€</li>
            </ul>
          </div>
          
          <button
            onClick={async () => {
              if (!window.confirm('Subkonten für alle Mitarbeiter und alle Departments initialisieren?\\n\\nFehlende Subkonten werden mit 0€ erstellt.')) {
                return;
              }
              
              try {
                const response = await axios.post(`${API}/setup/initialize-all-subaccounts`);
                alert('✅ ' + response.data.message);
              } catch (error) {
                console.error('Fehler bei der Initialisierung:', error);
                alert('❌ Fehler bei der Initialisierung: ' + (error.response?.data?.detail || error.message));
              }
            }}
            className="w-full sm:w-auto px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
          >
            🔄 Subkonten jetzt initialisieren
          </button>
        </div>
        
        {/* Department/8H Reset Section */}
        <div className="border-t pt-6 mt-6">
          <h4 className="text-lg font-semibold mb-2 text-red-600">🗑️ Daten zurücksetzen</h4>
          <p className="text-sm text-gray-600 mb-4">
            <strong>⚠️ ACHTUNG:</strong> Diese Aktion löscht ALLE Bestellungen, Zahlungsprotokolle und setzt alle Salden zurück. 
            Diese Aktion kann <strong>NICHT rückgängig gemacht werden</strong>!
          </p>
          
          {/* Type Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Was möchten Sie zurücksetzen?
            </label>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="department"
                  checked={resetType === 'department'}
                  onChange={(e) => {
                    setResetType(e.target.value);
                    setSelectedDepartment('');
                    setSelected8HEmployee('');
                  }}
                  className="mr-2"
                  disabled={isResetting}
                />
                <span>Wachabteilung</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="8h"
                  checked={resetType === '8h'}
                  onChange={(e) => {
                    setResetType(e.target.value);
                    setSelectedDepartment('');
                    setSelected8HEmployee('');
                  }}
                  className="mr-2"
                  disabled={isResetting}
                />
                <span>8H-Mitarbeiter</span>
              </label>
            </div>
          </div>
          
          {/* Department Selection */}
          {resetType === 'department' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Wachabteilung auswählen:
              </label>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                disabled={isResetting}
              >
                <option value="">-- Bitte wählen --</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* 8H Employee Selection */}
          {resetType === '8h' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                8H-Mitarbeiter auswählen:
              </label>
              <select
                value={selected8HEmployee}
                onChange={(e) => setSelected8HEmployee(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                disabled={isResetting}
              >
                <option value="">-- Bitte wählen --</option>
                {eightHEmployees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                  </option>
                ))}
              </select>
              {eightHEmployees.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  Keine 8H-Mitarbeiter gefunden.
                </p>
              )}
            </div>
          )}
          
          {/* Reset Button */}
          <button
            onClick={handleResetDepartment}
            disabled={(resetType === 'department' && !selectedDepartment) || 
                      (resetType === '8h' && !selected8HEmployee) || 
                      isResetting}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              ((resetType === 'department' && !selectedDepartment) || 
               (resetType === '8h' && !selected8HEmployee) || 
               isResetting)
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
          >
            {isResetting ? 'Wird zurückgesetzt...' : 'Zurücksetzen'}
          </button>
          
          {/* Confirmation Modal */}
          {showResetConfirmation && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-2xl p-6 max-w-md w-full">
                <div className="text-center mb-6">
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                    <svg className="h-10 w-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Bestätigung erforderlich</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Sie sind dabei, {resetType === 'department' ? (
                      <strong>{departments.find(d => d.id === selectedDepartment)?.name}</strong>
                    ) : (
                      <strong>8H-Mitarbeiter {eightHEmployees.find(e => e.id === selected8HEmployee)?.name}</strong>
                    )} zurückzusetzen.
                  </p>
                  <p className="text-sm text-red-600 font-semibold mb-4">
                    ⚠️ Diese Aktion kann NICHT rückgängig gemacht werden!
                  </p>
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bitte geben Sie <strong>"Löschen"</strong> ein, um fortzufahren:
                  </label>
                  <input
                    type="text"
                    value={confirmationText}
                    onChange={(e) => setConfirmationText(e.target.value)}
                    placeholder="Löschen"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    disabled={isResetting}
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowResetConfirmation(false);
                      setConfirmationText('');
                    }}
                    disabled={isResetting}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={confirmResetDepartment}
                    disabled={confirmationText !== 'Löschen' || isResetting}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                      confirmationText !== 'Löschen' || isResetting
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                  >
                    {isResetting ? 'Wird gelöscht...' : 'Jetzt löschen'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Breakfast Lock Management Section */}
        <div className="border-t pt-6 mt-6">
          <h4 className="text-lg font-semibold mb-2 text-orange-600">🔓 Frühstücks-Sperren aufheben</h4>
          <p className="text-sm text-gray-600 mb-4">
            Nach einem Frühstücks-Sponsoring wird die Bestellung für diese Wachabteilung gesperrt. 
            Hier können Sie die Sperre für einzelne Wachabteilungen aufheben.
          </p>
          
          {isLoadingLocks ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
              <p className="text-gray-600 mt-2 text-sm">Lade gesperrte Wachabteilungen...</p>
            </div>
          ) : lockedDepartments.length === 0 ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-green-700 font-medium">✅ Keine gesperrten Wachabteilungen für heute</p>
              <p className="text-green-600 text-sm mt-1">Alle Wachabteilungen können frei bestellen.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {lockedDepartments.map((locked) => (
                <div key={`${locked.department_id}-${locked.type}`} className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h5 className="font-semibold text-gray-900">{locked.department_name}</h5>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        locked.type === 'sponsoring_settings' 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {locked.type === 'sponsoring_settings' ? '🎁 Sponsoring-Sperre' : '🍳 Bestell-Sperre'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      🔒 Gesperrt von: <strong>{locked.closed_by}</strong>
                    </p>
                    {locked.closed_at && (
                      <p className="text-xs text-gray-500 mt-1">
                        Zeitpunkt: {new Date(locked.closed_at).toLocaleString('de-DE', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          timeZone: 'Europe/Berlin'
                        })}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => handleUnlockBreakfast(locked.department_id, locked.department_name)}
                    disabled={isUnlocking}
                    className={`ml-4 px-4 py-2 rounded-lg font-medium transition-colors ${
                      isUnlocking
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-orange-600 hover:bg-orange-700 text-white'
                    }`}
                  >
                    {isUnlocking ? 'Entsperre...' : 'Entsperren'}
                  </button>
                </div>
              ))}
              
              <button
                onClick={fetchLockedDepartments}
                disabled={isLoadingLocks}
                className="mt-4 text-sm text-blue-600 hover:text-blue-700 underline"
              >
                🔄 Liste aktualisieren
              </button>
            </div>
          )}
          
          {/* Sponsoring Reset Section */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-lg font-semibold mb-2 text-purple-600">🎁 Sponsoring zurücksetzen</h4>
            <p className="text-sm text-gray-600 mb-4">
              Hier nehmen Sie das Sponsoring des ausgewählten Tages vollständig zurück:
              Der Sponsor erhält den Betrag zurück und die Teilnehmer bezahlen wieder selbst.
              Alte Buchungen ohne genaue Rücknahmebelege müssen zuerst geprüft werden.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <select
                value={sponsoringResetDept}
                onChange={(e) => setSponsoringResetDept(e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
              >
                <option value="">-- Wachabteilung auswählen --</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
              
              <input
                type="date"
                value={sponsoringResetDate}
                onChange={(e) => setSponsoringResetDate(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
              />
              
              <button
                onClick={handleResetSponsoring}
                disabled={isResettingSponsoring || !sponsoringResetDept}
                className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                  isResettingSponsoring || !sponsoringResetDept
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                {isResettingSponsoring ? '⏳ Zurücksetzen...' : '🔄 Sponsoring zurücksetzen'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App Component


// Help Editor Tab Component - Rich Text Editor
const HelpEditorTab = ({ helpHtmlContent, setHelpHtmlContent, showPreview, setShowPreview, saveHelpContent }) => {
  const ReactQuill = React.lazy(() => import('react-quill'));

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-xl font-semibold mb-4">Hilfe-Inhalte bearbeiten</h3>
        <p className="text-sm text-gray-600 mb-4">
          Erstellen Sie Inhalte mit dem Editor. Formatierungen, Bilder und Links können über die Toolbar hinzugefügt werden.
          Änderungen werden für alle Wachabteilungen übernommen.
        </p>
        
        <div className="mb-4 flex gap-2">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            data-testid="help-toggle-preview"
          >
            {showPreview ? 'Editor anzeigen' : 'Vorschau anzeigen'}
          </button>
          <button
            onClick={saveHelpContent}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
            data-testid="help-save"
          >
            Speichern
          </button>
        </div>
        
        {showPreview ? (
          <div>
            <h4 className="text-sm font-medium mb-2">Vorschau:</h4>
            <div className="border border-gray-300 rounded-lg p-6 bg-gray-50 help-content">
              <div dangerouslySetInnerHTML={{ __html: helpHtmlContent }} />
            </div>
          </div>
        ) : (
          <React.Suspense fallback={<div className="h-64 bg-gray-50 rounded-lg animate-pulse" />}>
            <ReactQuill
              theme="snow"
              value={helpHtmlContent}
              onChange={setHelpHtmlContent}
              modules={quillModules}
              className="bg-white rounded-lg"
              style={{ minHeight: '300px' }}
              data-testid="help-rich-editor"
            />
          </React.Suspense>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// FEEDBACK MANAGEMENT TAB
// =============================================================================

const FeedbackManagementTab = () => {
  const [feedbackSettings, setFeedbackSettings] = useState(null);
  const [feedbackData, setFeedbackData] = useState({
    count: 0,
    avg_rating: 0,
    with_comment: 0,
    forwarded_to_payment: 0,
    rating_distribution: {},
    feedbacks: []
  });
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [settingsRes, feedbacksRes] = await Promise.all([
        axios.get(`${API}/feedback/settings`),
        axios.get(`${API}/feedback/all`)
      ]);
      setFeedbackSettings(settingsRes.data);
      setFeedbackData(feedbacksRes.data);
    } catch (error) {
      console.error('Error loading feedback data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetPopup = async () => {
    if (!window.confirm('Sind Sie sicher? Alle Benutzer werden das Feedback-Popup erneut sehen.')) {
      return;
    }
    
    try {
      setResetting(true);
      const response = await axios.post(`${API}/feedback/reset-popup`);
      setSuccessMessage(response.data.message);
      setShowSuccessNotification(true);
      await loadData();
    } catch (error) {
      alert(`Fehler: ${error.response?.data?.detail || error.message}`);
    } finally {
      setResetting(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return <div className="text-center py-8">Laden...</div>;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
        <MessageSquare className="text-purple-600" />
        Feedback System
      </h2>

      {/* Settings Card */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Einstellungen</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600">Aktuelle Popup-Version</label>
            <div className="text-3xl font-bold text-purple-600">
              {feedbackSettings?.version || 1}
            </div>
            <p className="text-xs text-gray-500">
              Beim Erhöhen sehen alle Benutzer das Popup erneut
            </p>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600">Status</label>
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
              feedbackSettings?.enabled 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {feedbackSettings?.enabled ? '✅ Aktiv' : '❌ Deaktiviert'}
            </div>
          </div>
        </div>

        <div className="mt-6 flex gap-4">
          <button
            onClick={resetPopup}
            disabled={resetting}
            className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            <RefreshCw size={18} className={resetting ? 'animate-spin' : ''} />
            {resetting ? 'Wird zurückgesetzt...' : 'Popup für alle zurücksetzen'}
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-purple-600">{feedbackData.count}</div>
          <div className="text-sm text-gray-500">Gesamt</div>
        </div>
        <div className="bg-white border rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-yellow-500 flex items-center justify-center gap-1">
            {feedbackData.avg_rating.toFixed(1)} <Star size={24} className="fill-yellow-400" />
          </div>
          <div className="text-sm text-gray-500">Ø Bewertung</div>
        </div>
        <div className="bg-white border rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-blue-600">{feedbackData.with_comment}</div>
          <div className="text-sm text-gray-500">Mit Kommentar</div>
        </div>
        <div className="bg-white border rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-green-600">{feedbackData.forwarded_to_payment}</div>
          <div className="text-sm text-gray-500">Weitergeleitet</div>
        </div>
      </div>

      {/* Rating Distribution */}
      {feedbackData.count > 0 && (
        <div className="bg-white border rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Bewertungsverteilung</h3>
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((rating) => {
              const count = feedbackData.rating_distribution[String(rating)] || 0;
              const percentage = feedbackData.count > 0 ? (count / feedbackData.count) * 100 : 0;
              return (
                <div key={rating} className="flex items-center gap-3">
                  <div className="flex items-center gap-1 w-16">
                    <span className="font-medium">{rating}</span>
                    <Star size={14} className="fill-yellow-400 text-yellow-400" />
                  </div>
                  <div className="flex-1 bg-gray-200 rounded-full h-4 overflow-hidden">
                    <div 
                      className="bg-yellow-400 h-full rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <div className="w-16 text-right text-sm text-gray-600">
                    {count} ({percentage.toFixed(0)}%)
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-800 mb-2">ℹ️ Hinweis</h4>
        <p className="text-sm text-blue-700">
          Das zentrale Feedback-Dashboard für <strong>alle Kantinen</strong> finden Sie in der Payment-App unter payment.fw-kantine.de.
          Hier sehen Sie die Feedbacks <strong>dieser Kantine</strong>.
        </p>
      </div>

      {/* All Feedbacks */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">
          Alle Feedbacks dieser Kantine ({feedbackData.feedbacks.length})
        </h3>
        
        {feedbackData.feedbacks.length === 0 ? (
          <p className="text-gray-500 text-center py-4">
            Noch keine Feedbacks vorhanden.
          </p>
        ) : (
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {feedbackData.feedbacks.map((feedback, index) => (
              <div key={feedback.id || index} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      {renderStars(feedback.rating)}
                      {feedback.forwarded_to_payment && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                          ✓ Weitergeleitet
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-gray-700">
                      {feedback.comment || <em className="text-gray-400">Kein Kommentar</em>}
                    </p>
                  </div>
                  <div className="text-xs text-gray-500 text-right ml-4">
                    <div>{new Date(feedback.timestamp).toLocaleString('de-DE')}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Success Notification */}
      {showSuccessNotification && (
        <SuccessNotification
          message={successMessage}
          onClose={() => setShowSuccessNotification(false)}
        />
      )}
    </div>
  );
};

// =============================================================================
// PAYMENT INTEGRATION TAB
// =============================================================================

const PaymentIntegrationTab = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [paymentCache, setPaymentCache] = useState([]);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Form state
  const [apiKey, setApiKey] = useState('');
  const [apiUrl, setApiUrl] = useState('https://payment.fw-kantine.de');
  const [wacheId, setWacheId] = useState('');
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    loadSettings();
    loadCache();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/payment/settings`);
      const data = response.data;
      
      if (data.configured) {
        setSettings(data);
        setApiUrl(data.api_url || 'https://payment.fw-kantine.de');
        setWacheId(data.wache_id || '');
        setEnabled(data.enabled !== undefined ? data.enabled : true);
      }
    } catch (error) {
      console.error('Error loading payment settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCache = async () => {
    try {
      const response = await axios.get(`${API}/payment/status-cache`);
      setPaymentCache(response.data.abteilungen || []);
    } catch (error) {
      console.error('Error loading payment cache:', error);
    }
  };

  const saveSettings = async () => {
    try {
      await axios.post(`${API}/payment/settings`, {
        api_key: apiKey || undefined,
        api_url: apiUrl,
        wache_id: wacheId,
        enabled: enabled
      });
      
      setSuccessMessage('✅ Einstellungen gespeichert');
      setShowSuccessNotification(true);
      setApiKey(''); // Clear input after save
      await loadSettings();
      await loadCache();
      
    } catch (error) {
      alert(`Fehler beim Speichern: ${error.response?.data?.detail || error.message}`);
    }
  };

  const testConnection = async () => {
    if (!apiKey) {
      alert('Bitte geben Sie den vollständigen API-Key ein, um die Verbindung zu testen.\n\nDer maskierte Key kann nicht zum Testen verwendet werden.');
      return;
    }
    
    try {
      setTesting(true);
      const response = await axios.post(`${API}/payment/test-connection`, {
        api_key: apiKey,
        api_url: apiUrl,
        wache_id: wacheId
      });
      
      console.log('Payment test response:', response.data);
      
      if (response.data.success) {
        alert(`✅ Verbindung erfolgreich!\n\nWache: ${response.data.wache_name}`);
      } else {
        alert(`❌ ${response.data.message || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      console.error('Payment test error:', error);
      console.error('Error response:', error.response);
      
      let errorMsg = 'Unbekannter Fehler';
      
      if (error.response) {
        // Server responded with error status
        errorMsg = error.response.data?.detail || 
                   error.response.data?.message || 
                   `HTTP ${error.response.status}: ${error.response.statusText}`;
      } else if (error.request) {
        // Request was made but no response
        errorMsg = 'Keine Antwort vom Server. Bitte prüfen Sie Ihre Internetverbindung.';
      } else {
        // Error in request setup
        errorMsg = error.message;
      }
      
      alert(`❌ Verbindung fehlgeschlagen:\n\n${errorMsg}\n\nBitte prüfen Sie die Browser-Console für Details (F12).`);
    } finally {
      setTesting(false);
    }
  };

  const syncNow = async () => {
    try {
      setSyncing(true);
      const response = await axios.post(`${API}/payment/sync`);
      
      setSuccessMessage(`✅ ${response.data.message}`);
      setShowSuccessNotification(true);
      
      await loadSettings();
      await loadCache();
      
    } catch (error) {
      alert(`Sync fehlgeschlagen: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">✅ Bezahlt</span>;
      case 'pending':
        return <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">⏳ Ausstehend</span>;
      case 'overdue':
        return <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">⚠️ Überfällig</span>;
      default:
        return <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">{status}</span>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getTimeSinceSync = () => {
    if (!settings?.last_sync) return 'Noch nie';
    
    const syncTime = new Date(settings.last_sync);
    const now = new Date();
    const diffMs = now - syncTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Min.`;
    if (diffHours < 24) return `vor ${diffHours} Std.`;
    return `vor ${Math.floor(diffHours / 24)} Tagen`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-6">💳 Payment Integration</h2>
        
        {/* API Configuration */}
        <div className="space-y-4 mb-6">
          <h3 className="text-lg font-semibold">API-Konfiguration</h3>
          
          <div>
            <label className="block text-sm font-medium mb-2">API URL</label>
            <input
              type="text"
              value={apiUrl}
              onChange={(e) => setApiUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="https://payment.fw-kantine.de"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Wache ID</label>
            <input
              type="text"
              value={wacheId}
              onChange={(e) => setWacheId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="z.B. wache_4200"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">
              API Key {settings?.api_key_masked && <span className="text-gray-500">(aktuell: {settings.api_key_masked})</span>}
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder={settings?.api_key_masked ? "Neuen Key eingeben zum Ändern" : "API-Key eingeben"}
            />
            <p className="text-xs text-gray-500 mt-1">
              Format: wk_[wache_nummer]_[hash]
            </p>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="mr-2"
              id="payment-enabled"
            />
            <label htmlFor="payment-enabled" className="text-sm font-medium">
              Payment Integration aktiviert
            </label>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={saveSettings}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              💾 Speichern
            </button>
            <button
              onClick={testConnection}
              disabled={testing || (!apiKey && !settings?.api_key_masked)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
            >
              {testing ? '⏳ Teste...' : '🔌 Verbindung testen'}
            </button>
          </div>
        </div>
        
        <hr className="my-6" />
        
        {/* Sync Status */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Payment Status</h3>
            <button
              onClick={syncNow}
              disabled={syncing || !settings?.configured}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400"
            >
              {syncing ? '⏳ Synchronisiere...' : '🔄 Jetzt synchronisieren'}
            </button>
          </div>
          
          {settings?.last_sync && (
            <div className="text-sm text-gray-600">
              Letzte Synchronisierung: {getTimeSinceSync()} 
              {settings.last_sync_status === 'success' && ' ✅'}
              {settings.last_sync_status === 'error' && ' ❌'}
              {settings.last_sync_status === 'timeout' && ' ⏱️'}
            </div>
          )}
          
          {/* Payment Status Table */}
          <PaymentDepartmentMapping api={API} refreshKey={paymentCache} />
          {paymentCache.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium">Abteilung</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Status</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Betrag</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Intervall</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Nächste Zahlung</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">User Login</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paymentCache.map((abt, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{abt.abteilung_name}</td>
                      <td className="px-4 py-3 text-sm">{getStatusBadge(abt.status)}</td>
                      <td className="px-4 py-3 text-sm">{abt.betrag.toFixed(2)} €</td>
                      <td className="px-4 py-3 text-sm">{abt.intervall}</td>
                      <td className="px-4 py-3 text-sm">
                        {formatDate(abt.naechste_zahlung_faellig)}
                        {abt.tage_bis_faellig !== null && (
                          <span className={`ml-2 text-xs ${
                            abt.tage_bis_faellig < 0 ? 'text-red-600' : 
                            abt.tage_bis_faellig < 7 ? 'text-yellow-600' : 'text-gray-500'
                          }`}>
                            ({abt.tage_bis_faellig < 0 ? `${Math.abs(abt.tage_bis_faellig)} Tage überfällig` : `in ${abt.tage_bis_faellig} Tagen`})
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {abt.user_username ? (
                          <div className="flex flex-col gap-1">
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded">{abt.user_username}</code>
                            {abt.payment_dashboard_url && (
                              <a
                                href={abt.payment_dashboard_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:underline"
                              >
                                Zum Dashboard →
                              </a>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Kein Zugang</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>Keine Payment-Daten verfügbar</p>
              <p className="text-sm mt-2">Bitte synchronisieren Sie zuerst die Daten</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Success Notification */}
      {showSuccessNotification && (
        <SuccessNotification
          message={successMessage}
          onClose={() => {
            setShowSuccessNotification(false);
            setSuccessMessage('');
          }}
        />
      )}
      
      {/* Scroll to Top Button */}
      <ScrollToTop />
    </div>
  );
};

export default DeveloperDashboard;

// =============================================================================
// Saldo-Diagnose Tab Component
// =============================================================================
const BalanceAuditTab = ({ allEmployees }) => {
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [auditResult, setAuditResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalcResult, setRecalcResult] = useState(null);
  const [integrityReports, setIntegrityReports] = useState([]);
  const [isCheckingAll, setIsCheckingAll] = useState(false);
  const [checkAllResult, setCheckAllResult] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [isLoadingTimeline, setIsLoadingTimeline] = useState(false);
  const [isFixingAll, setIsFixingAll] = useState(false);
  const [fixAllResult, setFixAllResult] = useState(null);

  useEffect(() => { loadIntegrityReports(); }, []);

  const loadIntegrityReports = async () => {
    try {
      const res = await axios.get(`${API}/developer/balance-integrity-reports`);
      setIntegrityReports(res.data);
    } catch (e) { /* ignore */ }
  };

  const runIntegrityCheckNow = async () => {
    setIsCheckingAll(true);
    setCheckAllResult(null);
    setFixAllResult(null);
    try {
      const res = await axios.post(`${API}/developer/balance-integrity-check-now`);
      setCheckAllResult(res.data);
      await loadIntegrityReports();
    } catch (e) {
      alert('Fehler: ' + (e.response?.data?.detail || e.message));
    } finally { setIsCheckingAll(false); }
  };

  const fixAllDiscrepancies = async () => {
    if (!window.confirm('ACHTUNG: Alle fehlerhaften Salden werden auf den berechneten Wert korrigiert. Fortfahren?')) return;
    setIsFixingAll(true);
    setFixAllResult(null);
    try {
      const res = await axios.post(`${API}/developer/fix-all-discrepancies`);
      setFixAllResult(res.data);
      await loadIntegrityReports();
      setCheckAllResult(null);
    } catch (e) {
      alert('Fehler: ' + (e.response?.data?.detail || e.message));
    } finally { setIsFixingAll(false); }
  };

  const filteredEmployees = allEmployees.filter(emp =>
    emp.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const runAudit = async (empId) => {
    const id = empId || selectedEmployeeId;
    if (!id) return;
    setIsLoading(true);
    setAuditResult(null);
    setRecalcResult(null);
    try {
      const res = await axios.get(`${API}/developer/balance-audit/${id}`);
      setAuditResult(res.data);
    } catch (e) {
      alert('Fehler: ' + (e.response?.data?.detail || e.message));
    } finally { setIsLoading(false); }
  };

  const runRecalculate = async (dryRun, deptId, isSub) => {
    if (!selectedEmployeeId) return;
    setIsRecalculating(true);
    try {
      let url = `${API}/developer/recalculate-balance/${selectedEmployeeId}?dry_run=${dryRun}`;
      if (deptId) url += `&dept_id=${deptId}`;
      if (isSub) url += `&is_subaccount=true`;
      const res = await axios.post(url);
      setRecalcResult(res.data);
      if (!dryRun) await runAudit(selectedEmployeeId);
    } catch (e) {
      alert('Fehler: ' + (e.response?.data?.detail || e.message));
    } finally { setIsRecalculating(false); }
  };

  const loadTimeline = async (deptId) => {
    if (!selectedEmployeeId) return;
    setIsLoadingTimeline(true);
    setTimeline(null);
    try {
      let url = `${API}/developer/balance-timeline/${selectedEmployeeId}`;
      if (deptId) url += `?dept_id=${deptId}`;
      const res = await axios.get(url);
      setTimeline(res.data);
    } catch (e) {
      alert('Fehler: ' + (e.response?.data?.detail || e.message));
    } finally { setIsLoadingTimeline(false); }
  };

  const fmtDate = (ds) => {
    if (!ds) return '-';
    return new Date(ds).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin'
    });
  };

  const fmtAmount = (v) => {
    const n = parseFloat(v) || 0;
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toFixed(2)}\u20AC`;
  };

  const safeNum = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  };

  // Renders a balance comparison row for one account
  const AccountAuditCard = ({ title, audit, accountType, deptId }) => {
    if (!audit || audit.error) {
      return (
        <div className="border rounded-lg p-4 mb-4 border-yellow-300 bg-yellow-50">
          <h4 className="font-semibold">{title}</h4>
          <p className="text-sm text-yellow-700">Fehler: {audit?.error || 'Keine Daten verfügbar'}</p>
        </div>
      );
    }
    const useCombined = audit.use_combined;
    const d = audit.diff || { has_discrepancy: false, breakfast: 0, drinks: 0, combined: 0 };
    const stored = audit.stored || { breakfast: 0, drinks: 0, combined: 0 };
    const expected = audit.expected || { breakfast: 0, drinks: 0, combined: 0 };
    const hasDisc = d.has_discrepancy;

    return (
      <div className={`border rounded-lg p-4 mb-4 ${hasDisc ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold">{title}</h4>
          <span className={`text-xs font-bold px-2 py-1 rounded-full ${hasDisc ? 'bg-red-200 text-red-800' : 'bg-green-200 text-green-800'}`}>
            {hasDisc ? 'ABWEICHUNG' : 'OK'}
          </span>
        </div>
        <table className="w-full text-sm mb-3">
          <thead className="bg-white/60">
            <tr>
              <th className="px-3 py-2 text-left">Konto</th>
              <th className="px-3 py-2 text-right">IST (gespeichert)</th>
              <th className="px-3 py-2 text-right">SOLL (berechnet)</th>
              <th className="px-3 py-2 text-right">Differenz</th>
            </tr>
          </thead>
          <tbody>
            {!useCombined && (
              <>
                <tr className="border-t">
                  <td className="px-3 py-2">Fr&uuml;hst&uuml;ck/Mittag</td>
                  <td className="px-3 py-2 text-right font-mono">{safeNum(stored.breakfast).toFixed(2)}&euro;</td>
                  <td className="px-3 py-2 text-right font-mono">{safeNum(expected.breakfast).toFixed(2)}&euro;</td>
                  <td className={`px-3 py-2 text-right font-bold ${Math.abs(safeNum(d.breakfast)) > 0.01 ? 'text-red-600' : 'text-green-600'}`}>
                    {Math.abs(safeNum(d.breakfast)) > 0.01 ? fmtAmount(d.breakfast) : 'OK'}
                  </td>
                </tr>
                <tr className="border-t">
                  <td className="px-3 py-2">Getr&auml;nke/Snacks</td>
                  <td className="px-3 py-2 text-right font-mono">{safeNum(stored.drinks).toFixed(2)}&euro;</td>
                  <td className="px-3 py-2 text-right font-mono">{safeNum(expected.drinks).toFixed(2)}&euro;</td>
                  <td className={`px-3 py-2 text-right font-bold ${Math.abs(safeNum(d.drinks)) > 0.01 ? 'text-red-600' : 'text-green-600'}`}>
                    {Math.abs(safeNum(d.drinks)) > 0.01 ? fmtAmount(d.drinks) : 'OK'}
                  </td>
                </tr>
              </>
            )}
            {useCombined && (
              <tr className="border-t">
                <td className="px-3 py-2">Gesamtkonto</td>
                <td className="px-3 py-2 text-right font-mono">{safeNum(stored.combined).toFixed(2)}&euro;</td>
                <td className="px-3 py-2 text-right font-mono">{safeNum(expected.combined).toFixed(2)}&euro;</td>
                <td className={`px-3 py-2 text-right font-bold ${Math.abs(safeNum(d.combined)) > 0.01 ? 'text-red-600' : 'text-green-600'}`}>
                  {Math.abs(safeNum(d.combined)) > 0.01 ? fmtAmount(d.combined) : 'OK'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {/* Summary info */}
        <div className="flex gap-4 text-xs text-gray-500">
          {audit.orders && <span>Orders: {audit.orders.total} ({audit.orders.cancelled} storn.)</span>}
          {audit.sponsoring && safeNum(audit.sponsoring.count) > 0 && (
            <span className="text-purple-600">Sponsoring: {audit.sponsoring.count}x (+{safeNum(audit.sponsoring.refund_total).toFixed(2)}&euro;)</span>
          )}
          {audit.payments && <span>Zahlungen: {audit.payments.total}</span>}
        </div>
        {/* Recalculate button for discrepancies */}
        {hasDisc && (
          <div className="mt-3 flex gap-2 flex-wrap">
            <button
              onClick={() => loadTimeline(deptId)}
              disabled={isLoadingTimeline}
              className="bg-blue-500 text-white px-3 py-1 rounded text-xs font-medium hover:bg-blue-600 disabled:opacity-50"
            >
              {isLoadingTimeline ? 'Lade...' : 'Timeline anzeigen'}
            </button>
            <button
              onClick={() => runRecalculate(true, deptId, accountType === 'sub')}
              disabled={isRecalculating}
              className="bg-yellow-500 text-white px-3 py-1 rounded text-xs font-medium hover:bg-yellow-600 disabled:opacity-50"
            >
              Vorschau
            </button>
            <button
              onClick={() => {
                if (window.confirm('Saldo f\u00FCr dieses Konto korrigieren?'))
                  runRecalculate(false, deptId, accountType === 'sub');
              }}
              disabled={isRecalculating}
              className="bg-red-600 text-white px-3 py-1 rounded text-xs font-medium hover:bg-red-700 disabled:opacity-50"
            >
              Korrigieren
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="balance-audit-tab">
      <h2 className="text-xl font-bold mb-4">Saldo-Diagnose</h2>
      <p className="text-gray-600 text-sm mb-6">
        Prüft Hauptkonto + alle Subkonten. Berücksichtigt Orders, Zahlungen, Sponsoring-Gutschriften.
      </p>

      {/* Nightly Integrity Check */}
      <div className="mb-8 bg-slate-50 border border-slate-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-slate-800">Automatischer Integritäts-Check</h3>
            <p className="text-xs text-slate-500">Läuft jede Nacht um 03:00 Uhr. Prüft Haupt- und Subkonten aller Mitarbeiter.</p>
          </div>
          <button
            data-testid="run-integrity-check-btn"
            onClick={runIntegrityCheckNow}
            disabled={isCheckingAll}
            className="bg-slate-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
          >
            {isCheckingAll ? 'Prüfe alle...' : 'Jetzt prüfen'}
          </button>
        </div>

        {integrityReports.length > 0 && (() => {
          const latest = integrityReports[0];
          const reportDate = fmtDate(latest.timestamp);
          return (
            <div className={`p-3 rounded-lg text-sm ${latest.status === 'ok' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-lg">{latest.status === 'ok' ? '✅' : '⚠️'}</span>
                <span className="font-medium">Letzter Check: {reportDate}{latest.triggered_by === 'manual' ? ' (manuell)' : ''}</span>
                <span className="text-gray-400">|</span>
                <span>{latest.total_checked} geprüft</span>
                <span className="text-gray-400">|</span>
                <span className={latest.discrepancies_count > 0 ? 'text-red-600 font-bold' : 'text-green-600'}>
                  {latest.discrepancies_count} Abweichungen
                </span>
              </div>
              {latest.discrepancies_count > 0 && latest.discrepancies && (
                <div className="mt-2 max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left">
                      <th className="px-2 py-1">Mitarbeiter</th>
                      <th className="px-2 py-1">Konto</th>
                      <th className="px-2 py-1">Abteilung</th>
                      <th className="px-2 py-1 text-right">Diff F</th>
                      <th className="px-2 py-1 text-right">Diff G</th>
                      <th className="px-2 py-1"></th>
                    </tr></thead>
                    <tbody>
                      {latest.discrepancies.map((disc, i) => (
                        <tr key={i} className="border-t border-red-100">
                          <td className="px-2 py-1 font-medium">{disc.employee_name}</td>
                          <td className="px-2 py-1 text-gray-500">{disc.account_type || 'Hauptkonto'}</td>
                          <td className="px-2 py-1 text-gray-500">{disc.department_name}</td>
                          <td className="px-2 py-1 text-right text-red-600">
                            {disc.use_combined ? '-' : (Math.abs(disc.diff.breakfast) > 0.01 ? fmtAmount(disc.diff.breakfast) : 'OK')}
                          </td>
                          <td className="px-2 py-1 text-right text-red-600">
                            {disc.use_combined ? fmtAmount(disc.diff.combined) : (Math.abs(disc.diff.drinks) > 0.01 ? fmtAmount(disc.diff.drinks) : 'OK')}
                          </td>
                          <td className="px-2 py-1">
                            <button onClick={() => { setSelectedEmployeeId(disc.employee_id); runAudit(disc.employee_id); }}
                              className="text-blue-600 hover:underline text-xs">Details</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-3 pt-3 border-t border-red-200">
                    <button
                      data-testid="fix-all-discrepancies-btn"
                      onClick={fixAllDiscrepancies}
                      disabled={isFixingAll}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                    >
                      {isFixingAll ? 'Korrigiere alle...' : `Alle ${latest.discrepancies_count} Abweichungen korrigieren`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {checkAllResult && !isCheckingAll && (
          <div className={`mt-2 p-3 rounded-lg text-sm ${checkAllResult.discrepancies_count > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
            {checkAllResult.message}
          </div>
        )}

        {fixAllResult && !isFixingAll && (
          <div className={`mt-2 p-3 rounded-lg text-sm ${fixAllResult.corrections_count > 0 ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
            <p className="font-medium mb-2">{fixAllResult.message}</p>
            {fixAllResult.corrections_count > 0 && (
              <div className="max-h-40 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left">
                    <th className="px-2 py-1">Mitarbeiter</th>
                    <th className="px-2 py-1">Konto</th>
                    <th className="px-2 py-1">Abteilung</th>
                    <th className="px-2 py-1 text-right">Alt F</th>
                    <th className="px-2 py-1 text-right">Neu F</th>
                    <th className="px-2 py-1 text-right">Alt G</th>
                    <th className="px-2 py-1 text-right">Neu G</th>
                  </tr></thead>
                  <tbody>
                    {fixAllResult.corrections.map((c, i) => (
                      <tr key={i} className="border-t border-green-100">
                        <td className="px-2 py-1 font-medium">{c.employee_name}</td>
                        <td className="px-2 py-1 text-gray-500">{c.account_type}</td>
                        <td className="px-2 py-1 text-gray-500">{c.department_name}</td>
                        <td className="px-2 py-1 text-right text-red-500 font-mono">{safeNum(c.old?.breakfast).toFixed(2)}</td>
                        <td className="px-2 py-1 text-right text-green-600 font-mono">{safeNum(c.new?.breakfast).toFixed(2)}</td>
                        <td className="px-2 py-1 text-right text-red-500 font-mono">{safeNum(c.old?.drinks).toFixed(2)}</td>
                        <td className="px-2 py-1 text-right text-green-600 font-mono">{safeNum(c.new?.drinks).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {fixAllResult.errors?.length > 0 && (
              <p className="mt-2 text-red-600 text-xs">Fehler: {fixAllResult.errors.join(', ')}</p>
            )}
          </div>
        )}
      </div>

      <hr className="mb-6" />

      {/* Employee Selection */}
      <h3 className="font-semibold mb-3">Einzeldiagnose</h3>
      <div className="mb-6 flex flex-col sm:flex-row gap-3">
        <input data-testid="balance-audit-search" type="text" placeholder="Mitarbeiter suchen..."
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
        <select data-testid="balance-audit-select" value={selectedEmployeeId}
          onChange={(e) => { setSelectedEmployeeId(e.target.value); setAuditResult(null); setRecalcResult(null); }}
          className="border border-gray-300 rounded-lg px-4 py-2 text-sm flex-1">
          <option value="">-- Mitarbeiter wählen --</option>
          {filteredEmployees.map(emp => (
            <option key={emp.id} value={emp.id}>{emp.name} ({emp.department_name || emp.department_id})</option>
          ))}
        </select>
        <button data-testid="balance-audit-run-btn" onClick={() => runAudit()}
          disabled={!selectedEmployeeId || isLoading}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-blue-700">
          {isLoading ? 'Prüfe...' : 'Diagnose starten'}
        </button>
      </div>

      {/* Audit Results */}
      {auditResult && (
        <div className="space-y-4">
          {/* Header */}
          <div className={`p-4 rounded-lg border-2 ${auditResult.overall_has_discrepancy ? 'border-red-400 bg-red-50' : 'border-green-400 bg-green-50'}`}>
            <div className="flex items-center gap-3">
              <span className="text-2xl">{auditResult.overall_has_discrepancy ? '⚠️' : '✅'}</span>
              <div>
                <h3 className="text-lg font-bold" data-testid="audit-employee-name">{auditResult.employee_name}</h3>
                <p className="text-sm text-gray-600">
                  {auditResult.department_name} | {auditResult.is_8h_service ? '8H-Dienst (nur Subkonten)' : 'Stamm/Gast'}
                </p>
              </div>
              <div className="ml-auto">
                <span className={`text-sm font-bold px-3 py-1 rounded-full ${auditResult.overall_has_discrepancy ? 'bg-red-200 text-red-800' : 'bg-green-200 text-green-800'}`} data-testid="audit-status">
                  {auditResult.overall_has_discrepancy ? 'ABWEICHUNG' : 'ALLES OK'}
                </span>
              </div>
            </div>
          </div>

          {/* Main Account */}
          {auditResult.main_audit && (
            <AccountAuditCard
              title={`Hauptkonto (${auditResult.main_audit.department_name})`}
              audit={auditResult.main_audit}
              accountType="main"
              deptId={null}
            />
          )}

          {!auditResult.main_audit && auditResult.is_8h_service && (
            <p className="text-sm text-gray-500 italic">8H-Mitarbeiter: Kein Hauptkonto vorhanden.</p>
          )}

          {/* Subaccounts */}
          {auditResult.subaccount_audits?.length > 0 && (
            <div>
              <h4 className="font-semibold mb-2 text-gray-700">Subkonten</h4>
              {(auditResult.subaccount_audits || []).map(sa => (
                <AccountAuditCard
                  key={sa.department_id}
                  title={`Subkonto: ${sa.department_name}`}
                  audit={sa}
                  accountType="sub"
                  deptId={sa.department_id}
                />
              ))}
            </div>
          )}

          {(auditResult.subaccount_audits?.length === 0 || !auditResult.subaccount_audits) && !auditResult.is_8h_service && (
            <p className="text-sm text-gray-400 italic">Keine aktiven Subkonten.</p>
          )}

          {/* Timeline */}
          {timeline && (
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold">Balance-Timeline: {timeline.department_name} {timeline.is_subaccount ? '(Subkonto)' : '(Hauptkonto)'}</h4>
                <button onClick={() => setTimeline(null)} className="text-gray-400 hover:text-gray-600 text-sm">Schlie&szlig;en</button>
              </div>
              <div className="mb-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="bg-blue-50 rounded p-2">
                  <div className="text-xs text-gray-500">Berechnet Fr&uuml;hst.</div>
                  <div className="font-bold">{safeNum(timeline.calculated_breakfast).toFixed(2)}&euro;</div>
                </div>
                <div className="bg-blue-50 rounded p-2">
                  <div className="text-xs text-gray-500">Gespeichert Fr&uuml;hst.</div>
                  <div className="font-bold">{safeNum(timeline.stored_breakfast).toFixed(2)}&euro;</div>
                </div>
                <div className={`rounded p-2 ${Math.abs(safeNum(timeline.diff_breakfast)) > 0.01 ? 'bg-red-50' : 'bg-green-50'}`}>
                  <div className="text-xs text-gray-500">Diff Fr&uuml;hst.</div>
                  <div className={`font-bold ${Math.abs(safeNum(timeline.diff_breakfast)) > 0.01 ? 'text-red-600' : 'text-green-600'}`}>
                    {fmtAmount(timeline.diff_breakfast)}
                  </div>
                </div>
                <div className={`rounded p-2 ${Math.abs(safeNum(timeline.diff_drinks)) > 0.01 ? 'bg-red-50' : 'bg-green-50'}`}>
                  <div className="text-xs text-gray-500">Diff Getr&auml;nke</div>
                  <div className={`font-bold ${Math.abs(safeNum(timeline.diff_drinks)) > 0.01 ? 'text-red-600' : 'text-green-600'}`}>
                    {fmtAmount(timeline.diff_drinks)}
                  </div>
                </div>
              </div>
              <div className="max-h-[400px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Datum</th>
                      <th className="px-2 py-1.5 text-left">Typ</th>
                      <th className="px-2 py-1.5 text-left">Beschreibung</th>
                      <th className="px-2 py-1.5 text-right">Effekt F</th>
                      <th className="px-2 py-1.5 text-right">Effekt G</th>
                      <th className="px-2 py-1.5 text-right">Laufend F</th>
                      <th className="px-2 py-1.5 text-right">Laufend G</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(timeline.timeline || []).map((e, i) => (
                      <tr key={i} className={`border-t ${e.type === 'order_cancelled' ? 'text-gray-400 line-through' : ''}`}>
                        <td className="px-2 py-1">{fmtDate(e.timestamp)}</td>
                        <td className="px-2 py-1">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            e.type === 'order' ? 'bg-blue-100 text-blue-700' :
                            e.type === 'order_cancelled' ? 'bg-gray-100 text-gray-500' :
                            'bg-green-100 text-green-700'
                          }`}>{e.type.includes('payment') ? 'Zahlung' : e.type === 'order_cancelled' ? 'Storno' : 'Order'}</span>
                        </td>
                        <td className="px-2 py-1 max-w-[200px] truncate">{e.description}</td>
                        <td className={`px-2 py-1 text-right font-mono ${safeNum(e.balance_effect_breakfast) !== 0 ? (safeNum(e.balance_effect_breakfast) > 0 ? 'text-green-600' : 'text-red-600') : 'text-gray-300'}`}>
                          {safeNum(e.balance_effect_breakfast) !== 0 ? fmtAmount(e.balance_effect_breakfast) : '-'}
                        </td>
                        <td className={`px-2 py-1 text-right font-mono ${safeNum(e.balance_effect_drinks) !== 0 ? (safeNum(e.balance_effect_drinks) > 0 ? 'text-green-600' : 'text-red-600') : 'text-gray-300'}`}>
                          {safeNum(e.balance_effect_drinks) !== 0 ? fmtAmount(e.balance_effect_drinks) : '-'}
                        </td>
                        <td className="px-2 py-1 text-right font-mono font-bold">{safeNum(e.running_breakfast).toFixed(2)}&euro;</td>
                        <td className="px-2 py-1 text-right font-mono font-bold">{safeNum(e.running_drinks).toFixed(2)}&euro;</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-2">{timeline.total_events || 0} Transaktionen. Laufendes Saldo zeigt den erwarteten Wert nach jeder Transaktion.</p>
            </div>
          )}

          {/* Recalc Result */}
          {recalcResult && (
            <div className={`border rounded-lg p-4 ${recalcResult.dry_run ? 'bg-gray-50 border-gray-300' : 'bg-green-50 border-green-300'}`}>
              <h4 className="font-semibold mb-2">
                {recalcResult.dry_run ? 'Vorschau' : '✅ Korrektur angewendet'} 
                {recalcResult.is_subaccount ? ` (Subkonto ${recalcResult.department_name})` : ' (Hauptkonto)'}
              </h4>
              <div className="text-sm space-y-1">
                {Object.entries(recalcResult.changes).map(([key, val]) =>
                  Math.abs(val.diff) > 0.01 ? (
                    <div key={key} className="flex items-center gap-2">
                      <span className="font-medium min-w-[100px]">{key}:</span>
                      <span className="text-red-600">{val.old.toFixed(2)}€</span>
                      <span>→</span>
                      <span className="text-green-600">{val.new.toFixed(2)}€</span>
                      <span className="text-gray-500">({fmtAmount(val.diff)})</span>
                    </div>
                  ) : null
                )}
              </div>
            </div>
          )}

          {/* Recent Orders & Payments (from main audit) */}
          {auditResult.main_audit?.recent_orders?.length > 0 && (
            <details className="text-sm">
              <summary className="font-semibold cursor-pointer mb-2">Letzte 10 Bestellungen (Hauptkonto)</summary>
              <table className="w-full text-xs bg-white border rounded">
                <thead className="bg-gray-50"><tr>
                  <th className="px-3 py-2 text-left">Datum</th>
                  <th className="px-3 py-2 text-left">Typ</th>
                  <th className="px-3 py-2 text-right">Betrag</th>
                  <th className="px-3 py-2 text-left">Info</th>
                </tr></thead>
                <tbody>
                  {auditResult.main_audit.recent_orders.map(o => (
                    <tr key={o.id} className="border-t">
                      <td className="px-3 py-2">{fmtDate(o.timestamp)}</td>
                      <td className="px-3 py-2">{o.type === 'breakfast' ? 'Frühstück' : o.type === 'drinks' ? 'Getränke' : 'Snacks'}</td>
                      <td className="px-3 py-2 text-right font-mono">{parseFloat(o.total_price).toFixed(2)}€</td>
                      <td className="px-3 py-2 text-gray-500">
                        {o.has_lunch && 'Mittag '}{o.is_sponsored && 'Gesponsert '}{o.is_sponsor_order && 'Sponsor'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}

          {auditResult.main_audit?.recent_payments?.length > 0 && (
            <details className="text-sm">
              <summary className="font-semibold cursor-pointer mb-2">Letzte 10 Zahlungen (Hauptkonto)</summary>
              <table className="w-full text-xs bg-white border rounded">
                <thead className="bg-gray-50"><tr>
                  <th className="px-3 py-2 text-left">Datum</th>
                  <th className="px-3 py-2 text-left">Typ</th>
                  <th className="px-3 py-2 text-right">Betrag</th>
                  <th className="px-3 py-2 text-left">Notiz</th>
                </tr></thead>
                <tbody>
                  {auditResult.main_audit.recent_payments.map(p => (
                    <tr key={p.id} className="border-t">
                      <td className="px-3 py-2">{fmtDate(p.timestamp)}</td>
                      <td className="px-3 py-2">{p.payment_type} ({p.action})</td>
                      <td className={`px-3 py-2 text-right font-mono ${parseFloat(p.amount) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {fmtAmount(p.amount)}
                      </td>
                      <td className="px-3 py-2 text-gray-500 max-w-[200px] truncate">{p.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}
    </div>
  );
};


// =============================================================================
// Lösch-Log Tab Component
// =============================================================================
const DeletionLogTab = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLog = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/developer/deletion-log`);
      setEntries(res.data);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLog(); }, []);

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatEuro = (v) => {
    const n = parseFloat(v) || 0;
    return n.toFixed(2).replace('.', ',') + ' €';
  };

  const hasNonZeroBalance = (entry) => {
    const m = entry.main_balances || {};
    if (m.breakfast || m.drinks_sweets || m.combined) return true;
    const subs = entry.subaccount_balances || {};
    for (const dept of Object.values(subs)) {
      if (dept.breakfast || dept.drinks || dept.combined) return true;
    }
    return false;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="deletion-log-loading">
        <RefreshCw className="animate-spin h-6 w-6 text-gray-400 mr-3" />
        <span className="text-gray-500">Lade Lösch-Log...</span>
      </div>
    );
  }

  return (
    <div data-testid="deletion-log-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Lösch-Log</h2>
          <p className="text-sm text-gray-500 mt-1">Protokoll aller gelöschten Mitarbeiter mit Kontoständen zum Zeitpunkt der Löschung</p>
        </div>
        <button
          onClick={fetchLog}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          data-testid="deletion-log-refresh"
        >
          <RefreshCw className="h-4 w-4" /> Aktualisieren
        </button>
      </div>

      {entries.length === 0 ? (
        <div className="text-center py-16 text-gray-400" data-testid="deletion-log-empty">
          <Trash2 className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p className="text-lg font-medium">Keine Einträge</p>
          <p className="text-sm mt-1">Es wurden noch keine Mitarbeiter gelöscht, seit das Log aktiv ist.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map((entry) => {
            const nonZero = hasNonZeroBalance(entry);
            return (
              <div
                key={entry.id}
                className={`border rounded-lg p-4 ${nonZero ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}
                data-testid={`deletion-log-entry-${entry.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <span className="font-semibold text-gray-900">{entry.employee_name}</span>
                    {entry.is_8h_service && (
                      <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">8H-Dienst</span>
                    )}
                    {entry.home_department_name && (
                      <span className="ml-2 text-xs text-gray-500">({entry.home_department_name})</span>
                    )}
                    {nonZero && (
                      <span className="ml-2 text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-medium">Restsaldo</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">{formatDate(entry.deleted_at)}</span>
                </div>

                {/* Balance details */}
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
                  {/* Main balances */}
                  {!entry.is_8h_service && (
                    <>
                      {(entry.main_balances?.breakfast !== 0) && (
                        <div className="flex justify-between bg-white/70 rounded px-3 py-1.5 border border-gray-100">
                          <span className="text-gray-600">Hauptkonto Frühstück</span>
                          <span className={`font-mono font-medium ${entry.main_balances?.breakfast < 0 ? 'text-red-600' : entry.main_balances?.breakfast > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                            {formatEuro(entry.main_balances?.breakfast)}
                          </span>
                        </div>
                      )}
                      {(entry.main_balances?.drinks_sweets !== 0) && (
                        <div className="flex justify-between bg-white/70 rounded px-3 py-1.5 border border-gray-100">
                          <span className="text-gray-600">Hauptkonto Getränke</span>
                          <span className={`font-mono font-medium ${entry.main_balances?.drinks_sweets < 0 ? 'text-red-600' : entry.main_balances?.drinks_sweets > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                            {formatEuro(entry.main_balances?.drinks_sweets)}
                          </span>
                        </div>
                      )}
                      {(entry.main_balances?.combined !== 0) && (
                        <div className="flex justify-between bg-white/70 rounded px-3 py-1.5 border border-gray-100">
                          <span className="text-gray-600">Hauptkonto Gesamt</span>
                          <span className={`font-mono font-medium ${entry.main_balances?.combined < 0 ? 'text-red-600' : entry.main_balances?.combined > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                            {formatEuro(entry.main_balances?.combined)}
                          </span>
                        </div>
                      )}
                    </>
                  )}

                  {/* Subaccount balances */}
                  {Object.entries(entry.subaccount_balances || {}).map(([deptId, dept]) => {
                    const anyNonZero = dept.breakfast || dept.drinks || dept.combined;
                    if (!anyNonZero) return null;
                    return (
                      <React.Fragment key={deptId}>
                        {dept.combined !== 0 && (
                          <div className="flex justify-between bg-white/70 rounded px-3 py-1.5 border border-gray-100">
                            <span className="text-gray-600">{dept.department_name} Gesamt</span>
                            <span className={`font-mono font-medium ${dept.combined < 0 ? 'text-red-600' : dept.combined > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                              {formatEuro(dept.combined)}
                            </span>
                          </div>
                        )}
                        {dept.breakfast !== 0 && !dept.combined && (
                          <div className="flex justify-between bg-white/70 rounded px-3 py-1.5 border border-gray-100">
                            <span className="text-gray-600">{dept.department_name} Frühstück</span>
                            <span className={`font-mono font-medium ${dept.breakfast < 0 ? 'text-red-600' : dept.breakfast > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                              {formatEuro(dept.breakfast)}
                            </span>
                          </div>
                        )}
                        {dept.drinks !== 0 && !dept.combined && (
                          <div className="flex justify-between bg-white/70 rounded px-3 py-1.5 border border-gray-100">
                            <span className="text-gray-600">{dept.department_name} Getränke</span>
                            <span className={`font-mono font-medium ${dept.drinks < 0 ? 'text-red-600' : dept.drinks > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                              {formatEuro(dept.drinks)}
                            </span>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {/* All zero */}
                  {!nonZero && (
                    <div className="flex items-center gap-2 text-gray-400 text-sm col-span-full">
                      <span>Alle Konten bei 0,00 €</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
